import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Spinner,
  Table,
  TabContent,
  TabPane,
} from "reactstrap";
import { exportControlStockExcel, exportControlStockPdf } from "./ControlStock/exporters";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/plugins/monthSelect/style.css";
import { driver } from "driver.js";
import type { DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import type { Instance as FlatpickrInstance } from "flatpickr/dist/types/instance";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainerReactTable";
import ReactECharts from "echarts-for-react";
import { requestJson } from "../../../helpers/api";
import baconLogo from "../../../assets/images/baconappen.png";
import DetailsModal from "./ControlStock/components/DetailsModal";
import ControlStockFiltersDrawer from "./ControlStock/components/ControlStockFiltersDrawer";
import MovementByDateTab from "./ControlStock/components/MovementByDateTab";
import StockTabsNav, { type StockTabId } from "./ControlStock/components/StockTabsNav";
import SummaryAccordion from "./ControlStock/components/SummaryAccordion";
import SyncModal from "./ControlStock/components/SyncModal";
import CollectionsModal from "./ControlStock/components/CollectionsModal";
import AgentWidget from "../../../Components/Agent/AgentWidget";
import useControlStockData from "./ControlStock/useControlStockData";
import { buildBranchColumns, buildFamilyLineColumns, buildStockColumns } from "./ControlStock/columns";
import {
  DATE_MODE_MAP,
  DETAIL_METRIC_OPTIONS,
  METRIC_FIELDS,
  MOVEMENT_DATE_METRIC_OPTIONS,
  NULL_OPTION_VALUE,
  PDF_METRIC_KEYS,
} from "./ControlStock/constants";
import {
  createMonthSelectPlugin,
  createWeekSelectPlugin,
  ensureDateValue,
  formatISODate,
  formatMonthYear,
  getMonthAnchorDate,
  getMonthRangeFromValue,
  getISOWeekString,
  getWeekAnchorDate,
  getWeekRangeFromValue,
  normalizeCustomRange,
} from "./ControlStock/dateUtils";
import {
  formatCurrency,
  formatHeaderDate,
  formatNumber,
  getPreviousDate,
  normalizeBranchKey,
} from "./ControlStock/formatters";
import {
  buildBranchResults,
  buildDetailTable,
  buildFamilyLineResults,
  buildMovementDateAvailability,
  buildMovementDateColumns,
  buildMovementRowsByDate,
  buildMovementRowsForMetric as buildMovementRowsForMetricSelector,
  buildSummaryCards,
  buildTotalsRow,
} from "./ControlStock/selectors";
import type {
  BranchResultRow,
  ControlStockRow,
  DateMode,
  DateSelectionState,
  DetailMetric,
  DiagnosticoSincronizacion,
  FamilyLineResultRow,
  MovementByDateRow,
  MovementDateMetric,
  MovementEntry,
} from "./ControlStock/types";

type CollectionGroup = {
  id: number;
  nombre: string;
  articulos: Set<string>;
};

const InformesControlStockPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [semanticTokens, setSemanticTokens] = useState<string[]>([]);
  const [dateSelection, setDateSelection] = useState<DateSelectionState>(() => {
    const now = new Date();
    const yesterdayDate = new Date(now);
    yesterdayDate.setDate(now.getDate() - 1);
    const today = formatISODate(yesterdayDate);
    return {
      mode: "day",
      day: today,
      week: getISOWeekString(yesterdayDate),
      month: today.slice(0, 7),
      rangeFrom: today,
      rangeTo: today,
    };
  });
  const [dateModeDropdownOpen, setDateModeDropdownOpen] = useState(false);
  const [differenceFilter, setDifferenceFilter] = useState<
    "todas" | "con" | "sin" | "positivas" | "negativas"
  >("todas");
  const [controlMode, setControlMode] = useState<
    "recetas" | "inventario"
  >("recetas");
  const [metricMode, setMetricMode] =
    useState<"cantidad" | "monto" | "ambos">("cantidad");
  const [activeStockTab, setActiveStockTab] = useState<StockTabId>(
    "stock-por-articulo",
  );
  const [movementDateMetric, setMovementDateMetric] =
    useState<MovementDateMetric>("compras");
  const [detailsMetric, setDetailsMetric] =
    useState<keyof ControlStockRow>("compras");
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [detailArticleTarget, setDetailArticleTarget] = useState<{
    idArticulo: string;
    articulo: string;
  } | null>(null);
  const [agentSummaryTable, setAgentSummaryTable] = useState<{
    title?: string;
    columns: string[];
    rows: any[];
  } | null>(null);
  const [agentSummaryPage, setAgentSummaryPage] = useState(1);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [isCollectionsModalOpen, setIsCollectionsModalOpen] = useState(false);
  const [isAgentOpen, setIsAgentOpen] = useState(false);
  const [syncDiagnostico, setSyncDiagnostico] =
    useState<DiagnosticoSincronizacion | null>(null);
  const [syncStatusError, setSyncStatusError] = useState<string | null>(null);
  const [syncDiagnosticoLoading, setSyncDiagnosticoLoading] = useState(false);
  const idmovGuideShownRef = useRef(false);
  const idmovGuidePendingRef = useRef(false);
  const idmovGuideTimeoutRef = useRef<number | null>(null);
  const idmovDriverRef = useRef<ReturnType<typeof driver> | null>(null);
  const idmovUserTouchedRef = useRef(false);
  const idmovGuideRetryRef = useRef(0);
  const [idmovGuideTick, setIdmovGuideTick] = useState(0);
  const [syncActionError, setSyncActionError] = useState<string | null>(null);
  const [syncingDays, setSyncingDays] = useState<number | null>(null);
  const [isSummaryOpen, setIsSummaryOpen] = useState(true);
  const [pdfDropdownOpen, setPdfDropdownOpen] = useState(false);
  const [pdfHideCosto, setPdfHideCosto] = useState(false);
  const [excelDropdownOpen, setExcelDropdownOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const showDifferenceChart = false;
  const ENABLE_IDMOV_DRIVER_GUIDE = false;

  const permisosUsuario = useMemo(() => {
    const raw =
      sessionStorage.getItem("authUser") ||
      localStorage.getItem("authUser") ||
      sessionStorage.getItem("user") ||
      localStorage.getItem("user");
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      const permisos =
        parsed?.usuario?.permisos ??
        parsed?.permisos ??
        parsed?.user?.permisos ??
        parsed?.data?.usuario?.permisos ??
        [];
      return Array.isArray(permisos) ? permisos : [];
    } catch {
      return [];
    }
  }, []);

  const canViewCollections =
    permisosUsuario.includes("informes.colecciones.ver") ||
    permisosUsuario.includes("general.solo_admin");
  const canEditCollections =
    permisosUsuario.includes("informes.colecciones.editar") ||
    permisosUsuario.includes("general.solo_admin");
  const canCloneCollections =
    permisosUsuario.includes("informes.colecciones.clonar") ||
    permisosUsuario.includes("general.solo_admin");
  const [pdfHiddenMetrics, setPdfHiddenMetrics] = useState({
    inventarioInicial: false,
    compras: false,
    remisionPositiva: false,
    remisionNegativa: false,
    ventas: false,
    ajusteNegativo: false,
    teorico: false,
    inventarioFinal: false,
    diferencia: false,
  });
  const [inventoryMovementFilter, setInventoryMovementFilter] = useState<{
    initial: string;
    final: string;
  }>({ initial: "", final: "" });
  const [inventoryMovementByBranch, setInventoryMovementByBranch] = useState<{
    initial: Record<string, string>;
    final: Record<string, string>;
  }>({ initial: {}, final: {} });
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [isSelectingRange, setIsSelectingRange] = useState(false);
  const [groupViewMode, setGroupViewMode] = useState<"sin" | "coleccion">("coleccion");
  const [collectionFilter, setCollectionFilter] = useState("todas");
  const [branchView, setBranchView] = useState<string>("todas");
  const [idmovInitialOpen, setIdmovInitialOpen] = useState(false);
  const [idmovFinalOpen, setIdmovFinalOpen] = useState(false);
  const [differenceChartPage, setDifferenceChartPage] = useState(1);
  const differenceChartPageSize = 50;
  const [collectionGroups, setCollectionGroups] = useState<CollectionGroup[]>([]);
  const [, setCollectionsLoading] = useState(false);
  const [, setCollectionsError] = useState<string | null>(null);
  const [collectionsRefreshKey, setCollectionsRefreshKey] = useState(0);
  useEffect(() => {
    if (groupViewMode !== "coleccion" || !canViewCollections) {
      setCollectionGroups([]);
      setCollectionsError(null);
      setCollectionsLoading(false);
      return;
    }
    if (isCollectionsModalOpen) {
      return;
    }
    let cancelled = false;
    const cargar = async () => {
      setCollectionsLoading(true);
      setCollectionsError(null);
      try {
        const data = await requestJson("/api/informes/colecciones");
        const colecciones = Array.isArray(data) ? data : [];
        const detailed = await Promise.all(
          colecciones.map(async (coleccion: { id: number; nombre?: string | null }) => {
            const nombre = String(coleccion?.nombre ?? `Colección ${coleccion.id}`);
            try {
              const items = await requestJson(
                `/api/informes/colecciones/${coleccion.id}/articulos`,
              );
              const articulos = new Set(
                Array.isArray(items)
                  ? items
                      .map((id) => String(id ?? "").trim())
                      .filter((id) => id.length > 0)
                  : [],
              );
              return { id: coleccion.id, nombre, articulos } as CollectionGroup;
            } catch {
              return { id: coleccion.id, nombre, articulos: new Set() } as CollectionGroup;
            }
          }),
        );
        if (!cancelled) {
          setCollectionGroups(detailed);
        }
      } catch {
        if (!cancelled) {
          setCollectionGroups([]);
          setCollectionsError("No se pudieron cargar las colecciones.");
        }
      } finally {
        if (!cancelled) {
          setCollectionsLoading(false);
        }
      }
    };
    void cargar();
    return () => {
      cancelled = true;
    };
  }, [canViewCollections, groupViewMode, isCollectionsModalOpen, collectionsRefreshKey]);
  const forceCloseDriver = useCallback(() => {
    if (idmovDriverRef.current) {
      try {
        idmovDriverRef.current.destroy();
      } catch {
        // ignore
      }
      idmovDriverRef.current = null;
    }
    document
      .querySelectorAll(
        ".driver-overlay, .driver-popover, .driver-popover-shadow, .driver-popover-active, .driver-active-element, .driver-highlighted-element",
      )
      .forEach((node) => {
        if (node.parentNode) {
          node.parentNode.removeChild(node);
        }
      });
    document
      .querySelectorAll(".driver-active-element, .driver-highlighted-element")
      .forEach((node) => {
        node.classList.remove("driver-active-element", "driver-highlighted-element");
      });
  }, []);
  const handleInventoryMovementFilterChange = useCallback(
    (type: "initial" | "final", value: string) => {
      idmovUserTouchedRef.current = true;
      forceCloseDriver();
      setInventoryMovementFilter((prev) => ({
        ...prev,
        [type]: value,
      }));
    },
    [forceCloseDriver],
  );
  const dateRange = useMemo(() => {
    const today = formatISODate(new Date());
    let start = today;
    let end = today;
    let displayLabel = formatHeaderDate(today);
    let previousDate = getPreviousDate(today);
    const selection = dateSelection;

    if (selection.mode === "day") {
      const dayValue = ensureDateValue(selection.day, today);
      start = dayValue;
      end = dayValue;
      previousDate = getPreviousDate(dayValue);
      displayLabel = formatHeaderDate(dayValue);
    } else if (selection.mode === "week") {
      const { start: weekStart, end: weekEnd } = getWeekRangeFromValue(selection.week, today);
      start = weekStart;
      end = weekEnd;
      previousDate = getPreviousDate(start);
      displayLabel = `${formatHeaderDate(weekStart)} - ${formatHeaderDate(weekEnd)}`;
    } else if (selection.mode === "month") {
      const {
        start: monthStart,
        end: monthEnd,
        previousDate: monthPrevious,
      } = getMonthRangeFromValue(selection.month, today);
      start = monthStart;
      end = monthEnd;
      previousDate = monthPrevious;
      displayLabel =
        formatMonthYear(selection.month) ||
        `${formatHeaderDate(monthStart)} - ${formatHeaderDate(monthEnd)}`;
    } else {
      const { start: rangeStart, end: rangeEnd } = normalizeCustomRange(
        selection.rangeFrom,
        selection.rangeTo,
        today,
      );
      start = rangeStart;
      end = rangeEnd;
      previousDate = getPreviousDate(rangeStart);
      displayLabel = `${formatHeaderDate(rangeStart)} - ${formatHeaderDate(rangeEnd)}`;
    }

    const queryStartDate =
      selection.mode === "month" ? previousDate || start : start;
    const queryEndDate = end;

    return {
      mode: selection.mode,
      startDate: start,
      endDate: end,
      displayLabel,
      previousDate,
      previousLabel: formatHeaderDate(previousDate),
      query: {
        fechaDesde: queryStartDate,
        fechaHasta: queryEndDate,
        ...(selection.mode === "day" ? { fecha: end } : {}),
      },
      filterKey: `${selection.mode}:${start}:${end}`,
    };
  }, [dateSelection]);
  const {
    serverOptions,
    serverFilter,
    setServerFilter,
    branchOptions,
    branchFilter,
    setBranchFilter,
    familyFilter,
    setFamilyFilter,
    lineFilter,
    setLineFilter,
    familyOptions,
    lineOptions,
    familyLabelMap,
    lineLabelMap,
    recipeArticleSet,
    recipeCatalogStatus,
    records,
    recordsLoading,
    recordsError,
    catalogMap,
  } = useControlStockData(dateRange);
  const resolvedDateLabel = dateRange.displayLabel;
  const previousDateLabel = dateRange.previousLabel;
  const finalDateLabel = formatHeaderDate(dateRange.endDate);
  const movementDateLabel = `${formatHeaderDate(dateRange.startDate)} - ${formatHeaderDate(
    dateRange.endDate,
  )}`;
  const currentModeOption = DATE_MODE_MAP[dateSelection.mode];
  const activeDateFilterKey = dateRange.filterKey;

  useEffect(() => {
    if (branchFilter.length === 0) {
      setBranchView("todas");
      return;
    }
    if (branchFilter.length === 1) {
      const only = branchFilter[0];
      setBranchView(only || "todas");
      return;
    }
    if (branchView !== "todas" && !branchFilter.includes(branchView)) {
      setBranchView("todas");
    }
  }, [branchFilter, branchView]);

  const fetchSyncDiagnostico = useCallback(async () => {
    setSyncDiagnosticoLoading(true);
    try {
      setSyncStatusError(null);
      const respuesta = await requestJson<DiagnosticoSincronizacion>(
        "/api/informes/sincronizacion/diagnostico",
      );
      setSyncDiagnostico(respuesta ?? null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "No se pudo cargar el diagnóstico.";
      setSyncStatusError(message);
    } finally {
      setSyncDiagnosticoLoading(false);
    }
  }, []);

  const handleOpenSyncModal = useCallback(() => {
    setIsSyncModalOpen(true);
    setSyncActionError(null);
    void fetchSyncDiagnostico();
  }, [fetchSyncDiagnostico]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const onOpenFromHeader = () => {
      handleOpenSyncModal();
    };
    window.addEventListener("control-stock-open-sync-modal", onOpenFromHeader);
    return () => {
      window.removeEventListener("control-stock-open-sync-modal", onOpenFromHeader);
    };
  }, [handleOpenSyncModal]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const onOpenAgentFromHeader = () => {
      setIsAgentOpen(true);
    };
    window.addEventListener("control-stock-open-agent-widget", onOpenAgentFromHeader);
    return () => {
      window.removeEventListener("control-stock-open-agent-widget", onOpenAgentFromHeader);
    };
  }, []);

  useEffect(() => {
    if (!isSyncModalOpen) {
      return;
    }
    const interval = window.setInterval(() => {
      void fetchSyncDiagnostico();
    }, 5000);
    return () => window.clearInterval(interval);
  }, [isSyncModalOpen, fetchSyncDiagnostico]);

  const handleStartSync = useCallback(
    async (days: 4 | 7 | 15 | 20 | 31) => {
      setSyncingDays(days);
      setSyncActionError(null);
      try {
        await requestJson("/api/informes/sincronizar", {
          method: "POST",
          body: JSON.stringify({ days }),
        });
        await fetchSyncDiagnostico();
      } catch (error) {
        let message = error instanceof Error ? error.message : "No se pudo iniciar la sincronización.";
        // El 504 de nginx devuelve HTML — mostrar mensaje amigable en su lugar
        if (message.includes("<html") || message.includes("504") || message.includes("Gateway Time-out")) {
          message = "La sincronización tardó demasiado (timeout). El proceso puede estar corriendo en segundo plano. Verificá el estado en unos minutos.";
        }
        setSyncActionError(message);
      } finally {
        setSyncingDays(null);
      }
    },
    [fetchSyncDiagnostico],
  );

  const toggleDateModeDropdown = () =>
    setDateModeDropdownOpen((prev) => !prev);

  const parseDateForPicker = (value?: string) => {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return null;
    }
    const parsed = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const updateDateSelection = (updates: Partial<DateSelectionState>) => {
    setDateSelection((prev) => ({ ...prev, ...updates }));
  };

  const handleModeChange = (mode: DateMode) => {
    setDateSelection((prev) => {
      if (prev.mode === mode) {
        return prev;
      }
      const now = new Date();
      const today = formatISODate(now);
      const next: DateSelectionState = { ...prev, mode };
      if (mode === "day" && !prev.day) {
        next.day = today;
      }
      if (mode === "week" && !prev.week) {
        next.week = getISOWeekString(now);
      }
      if (mode === "month" && !prev.month) {
        next.month = today.slice(0, 7);
      }
      if (mode === "range") {
        if (!prev.rangeFrom) {
          next.rangeFrom = today;
        }
        if (!prev.rangeTo) {
          next.rangeTo = today;
        }
      }
      return next;
    });
    setDateModeDropdownOpen(false);
  };

  const renderDateModeInput = () => {
    const sharedProps = {
      style: { minWidth: 0 },
    };
    if (dateSelection.mode === "range") {
      const startDate = parseDateForPicker(dateSelection.rangeFrom);
      const hasDistinctRange =
        dateSelection.rangeTo !== dateSelection.rangeFrom &&
        dateSelection.rangeTo.trim().length > 0;
      const endDate = hasDistinctRange
        ? parseDateForPicker(dateSelection.rangeTo)
        : null;
      const pickerValues: Date[] =
        !startDate || isSelectingRange
          ? []
          : endDate
            ? [startDate, endDate]
            : [startDate];

      const handleRangeChange = (
        dates: Date[],
        _dateStr: string,
        instance?: FlatpickrInstance,
      ) => {
        if (!dates || dates.length === 0) {
          return;
        }
        const [start, end] = dates;
        if (!start) {
          return;
        }
        const startStr = formatISODate(start);
        if (!end) {
          setIsSelectingRange(true);
          updateDateSelection({ rangeFrom: startStr, rangeTo: startStr });
          if (instance && instance.isOpen) {
            // En algunos navegadores el input queda undefined brevemente; reabrimos solo si existe.
            window.setTimeout(() => {
              try {
                instance.open();
              } catch {
                // ignorado
              }
            }, 0);
          }
          return;
        }
        setIsSelectingRange(false);
        const endStr = formatISODate(end);
        const normalized = normalizeCustomRange(startStr, endStr, startStr);
        updateDateSelection({
          rangeFrom: normalized.start,
          rangeTo: normalized.end,
        });
      };

      return (
        <div className="flex-grow-1">
          <Flatpickr
            className="form-control rounded-end flag-input"
            value={pickerValues.length > 0 ? pickerValues : undefined}
            options={{ mode: "range", dateFormat: "Y-m-d", closeOnSelect: false }}
            onChange={handleRangeChange}
          />
        </div>
      );
    }
    if (dateSelection.mode === "week") {
      const today = formatISODate(new Date());
      const weekDate = getWeekAnchorDate(dateSelection.week, today);
      return (
        <Flatpickr
          className="form-control rounded-end flag-input"
          value={weekDate ? [weekDate] : undefined}
          options={{
            mode: "single",
            dateFormat: "Y-m-d",
            weekNumbers: true,
            plugins: [createWeekSelectPlugin()],
          }}
          onChange={(dates: Date[]) => {
            if (!dates || dates.length === 0 || !dates[0]) {
              return;
            }
            updateDateSelection({ week: getISOWeekString(dates[0]) });
          }}
        />
      );
    }
    if (dateSelection.mode === "month") {
      const today = formatISODate(new Date());
      const monthDate = getMonthAnchorDate(dateSelection.month, today);
      return (
        <Flatpickr
          className="form-control rounded-end flag-input"
          value={monthDate ? [monthDate] : undefined}
          options={{
            mode: "single",
            altInput: true,
            plugins: [
              createMonthSelectPlugin({
                shorthand: true,
                dateFormat: "Y-m",
                altFormat: "F Y",
              }),
            ],
          }}
          onChange={(dates: Date[]) => {
            if (!dates || dates.length === 0 || !dates[0]) {
              return;
            }
            const selected = dates[0];
            const value = `${selected.getFullYear()}-${String(
              selected.getMonth() + 1,
            ).padStart(2, "0")}`;
            updateDateSelection({ month: value });
          }}
        />
      );
    }
    return (
      <Input
        type="date"
        className="form-control rounded-end flag-input"
        value={dateSelection.day}
        onChange={(event) =>
          updateDateSelection({ day: event.target.value })
        }
        {...sharedProps}
      />
    );
  };

  useEffect(() => {
    document.title = "Control de Stock | Informes | Saas";
  }, []);

  useEffect(() => {
    let activo = true;
    const cargarLogo = async () => {
      try {
        const fuentes = [baconLogo, "/baconappen_529x124.png", "/baconappen.png"];
        for (const fuente of fuentes) {
          if (!fuente) {
            continue;
          }
          try {
            const respuesta = await fetch(fuente);
            if (!respuesta.ok) {
              continue;
            }
            const blob = await respuesta.blob();
            const lector = new FileReader();
            const resultado = await new Promise<string>((resolve, reject) => {
              lector.onloadend = () => {
                if (typeof lector.result === "string") {
                  resolve(lector.result);
                } else {
                  reject(new Error("No pudimos leer el logo."));
                }
              };
              lector.onerror = () => reject(new Error("No pudimos leer el logo."));
              lector.readAsDataURL(blob);
            });
            if (activo) {
              setLogoDataUrl(resultado);
            }
            return;
          } catch {
            continue;
          }
        }
      } catch {
        // ignorado
      }
    };
    void cargarLogo();
    return () => {
      activo = false;
    };
  }, []);

  const hasRecipeMatchesInRecords = useMemo(() => {
    if (recipeCatalogStatus !== "ready") {
      return false;
    }
    return records.some((row) => {
      const normalizedArticleId = (row.idArticulo ?? "")
        .trim()
        .toUpperCase();
      return (
        normalizedArticleId.length > 0 &&
        recipeArticleSet.has(normalizedArticleId)
      );
    });
  }, [records, recipeArticleSet, recipeCatalogStatus]);

  const baseFilteredData = useMemo(() => {
    const collectionAssignmentMap = new Map<string, string>();
    collectionGroups.forEach((collection) => {
      collection.articulos.forEach((id) => {
        collectionAssignmentMap.set(id, String(collection.id));
      });
    });
    const normalizeText = (value: string) =>
      value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
    const query = normalizeText(searchTerm || "");
    const semanticList = Array.isArray(semanticTokens)
      ? semanticTokens.map((token) => normalizeText(String(token))).filter(Boolean)
      : [];
    const semanticFamilySet = new Set<string>();
    const semanticCollectionSet = new Set<string>();
    if (semanticList.length > 0) {
      familyOptions.forEach((opt) => {
        const norm = normalizeText(opt.label);
        if (semanticList.some((token) => norm.includes(token))) {
          semanticFamilySet.add(norm);
        }
      });
      collectionGroups.forEach((group) => {
        const norm = normalizeText(group.nombre || "");
        if (semanticList.some((token) => norm.includes(token))) {
          semanticCollectionSet.add(norm);
        }
      });
    }
    const stopwords = new Set([
      "de",
      "del",
      "la",
      "el",
      "los",
      "las",
      "y",
      "o",
      "u",
      "a",
      "en",
      "por",
      "con",
      "sin",
      "para",
      "al",
      "como",
      "que",
    ]);
    const tokens = query
      .split(/\s+/)
      .map((token) => token.trim())
      .filter((token) => token.length >= 2 && !stopwords.has(token));
    const baseMatches: typeof records = [];
    let strictCount = 0;

    for (const row of records) {
      const matchesServer =
        !serverFilter || row.server === serverFilter;
      const branchScope =
        branchView && branchView !== "todas" ? [branchView] : branchFilter;
      const matchesBranch =
        branchScope.length === 0 ||
        branchScope.some(
          (selected) =>
            normalizeBranchKey(row.sucursal) === normalizeBranchKey(selected),
        );
      const matchesDate =
        !activeDateFilterKey ||
        row.__rangeKey === activeDateFilterKey ||
        row.fechaReferencia === resolvedDateLabel;
      const normalizedArticleId = (row.idArticulo ?? "")
        .trim()
        .toUpperCase();
      const isRecipeItem =
        normalizedArticleId.length > 0 &&
        recipeArticleSet.has(normalizedArticleId);
      const hasVentasValue = Number(row.ventas ?? 0) !== 0;
      const matchesControl = (() => {
        if (controlMode === "recetas") {
          if (
            recipeCatalogStatus !== "ready" ||
            !hasRecipeMatchesInRecords
          ) {
            return true;
          }
          return isRecipeItem || hasVentasValue;
        }
        if (recipeCatalogStatus !== "ready" || !hasRecipeMatchesInRecords) {
          return true;
        }
        return !isRecipeItem;
      })();
      const catalogEntry = normalizedArticleId
        ? catalogMap[normalizedArticleId]
        : undefined;
      const familiaIdFromCatalog = catalogEntry?.familiaId ?? row.__familiaId ?? null;
      const lineaIdFromCatalog = catalogEntry?.lineaId ?? row.__lineaId ?? null;
      const matchesFamily =
        familyFilter === "todas" ||
        (familyFilter === NULL_OPTION_VALUE
          ? familiaIdFromCatalog == null
          : familiaIdFromCatalog === familyFilter);
      const matchesLinea =
        lineFilter === "todas" ||
        (lineFilter === NULL_OPTION_VALUE
          ? lineaIdFromCatalog == null
          : lineaIdFromCatalog === lineFilter);
      const haystackId = normalizeText(row.idArticulo || "");
      const haystackArticulo = normalizeText(row.articulo || "");
    const matchesSearchStrict =
      tokens.length === 0 ||
      tokens.every(
        (token) =>
          haystackId.includes(token) || haystackArticulo.includes(token),
      );
      const collectionId = normalizedArticleId
        ? collectionAssignmentMap.get(normalizedArticleId)
        : undefined;
      const matchesCollection =
        collectionFilter === "todas" ||
        (collectionFilter === NULL_OPTION_VALUE
          ? !collectionId
          : collectionId === collectionFilter);
      const familyLabel =
        row.familia ??
        (familiaIdFromCatalog != null
          ? familyOptions.find((opt) => opt.value === familiaIdFromCatalog)?.label
          : null) ??
        "";
      const lineLabel =
        row.linea ??
        (lineaIdFromCatalog != null
          ? lineOptions.find((opt) => opt.value === lineaIdFromCatalog)?.label
          : null) ??
        "";
      const collectionLabel = collectionId
        ? collectionGroups.find((c) => String(c.id) === String(collectionId))?.nombre ?? ""
        : "";
      const normalizedFamily = normalizeText(String(familyLabel || ""));
      const normalizedLine = normalizeText(String(lineLabel || ""));
      const normalizedCollection = normalizeText(String(collectionLabel || ""));
      const hasSemanticTargets =
        semanticFamilySet.size > 0 || semanticCollectionSet.size > 0;
      const matchesSemantic =
        semanticList.length === 0
          ? true
          : hasSemanticTargets
            ? semanticFamilySet.has(normalizedFamily) ||
              semanticCollectionSet.has(normalizedCollection)
            : semanticList.some(
                (token) =>
                  normalizedFamily.includes(token) ||
                  normalizedLine.includes(token) ||
                  normalizedCollection.includes(token),
              );
      const hasAnyMetricValue = METRIC_FIELDS.some(
        (field) => Number(row[field] ?? 0) !== 0,
      );
      const matchesBase =
        matchesServer &&
        matchesBranch &&
        matchesDate &&
        matchesControl &&
        matchesFamily &&
        matchesLinea &&
        matchesCollection &&
        matchesSemantic &&
        hasAnyMetricValue
      ;

      if (matchesBase) {
        baseMatches.push(row);
        if (matchesSearchStrict) {
          strictCount += 1;
        }
      }
    }

    const searchMode =
      tokens.length === 0
        ? semanticList.length > 0
          ? "semantic"
          : "none"
        : strictCount > 0
          ? "exact"
          : "approx";

    if (tokens.length === 0) {
      return {
        rows: baseMatches,
        searchMode,
        searchTokens: semanticList.length > 0 ? semanticList : [],
        searchExactCount: 0,
        searchApproxCount: 0,
      };
    }

    if (strictCount > 0) {
      const exactRows = baseMatches.filter((row) => {
          const haystackId = normalizeText(row.idArticulo || "");
          const haystackArticulo = normalizeText(row.articulo || "");
          return tokens.every(
            (token) =>
              haystackId.includes(token) || haystackArticulo.includes(token),
          );
        });
      return {
        rows: exactRows,
        searchMode,
        searchTokens: tokens,
        searchExactCount: exactRows.length,
        searchApproxCount: 0,
      };
    }

    const approxRows = baseMatches.filter((row) => {
      const haystackId = normalizeText(row.idArticulo || "");
      const haystackArticulo = normalizeText(row.articulo || "");
      return tokens.some(
        (token) =>
          haystackId.includes(token) || haystackArticulo.includes(token),
      );
    });
    return {
      rows: approxRows,
      searchMode,
      searchTokens: tokens,
      searchExactCount: 0,
      searchApproxCount: approxRows.length,
    };
  }, [
    records,
    branchFilter,
    branchView,
    activeDateFilterKey,
    resolvedDateLabel,
    controlMode,
    familyFilter,
    lineFilter,
    recipeArticleSet,
    recipeCatalogStatus,
    hasRecipeMatchesInRecords,
    searchTerm,
    semanticTokens,
    serverFilter,
    catalogMap,
    collectionGroups,
    collectionFilter,
  ]);

  const filteredData = useMemo(() => {
    const selectedInitial = inventoryMovementFilter.initial.trim();
    const selectedFinal = inventoryMovementFilter.final.trim();

    const sumInventoryByMovement = (
      details: MovementEntry[] | undefined,
      selectedId: string,
      fallback: number,
    ) => {
      if (!selectedId) {
        return fallback;
      }
      if (!Array.isArray(details) || details.length === 0) {
        return 0;
      }
      return details.reduce((acc, entry) => {
        if (entry.idMovimiento == null) {
          return acc;
        }
        return String(entry.idMovimiento).trim() === selectedId
          ? acc + Number(entry.cantidad ?? 0)
          : acc;
      }, 0);
    };

    const mappedRows = baseFilteredData.rows
      .map((row) => {
        const initialDetails = row.__details?.inventarioInicial ?? [];
        const finalDetails = row.__details?.inventarioFinal ?? [];
        const branchKey = normalizeBranchKey(row.sucursal);
        const initialSelectedId =
          branchView === "todas"
            ? inventoryMovementByBranch.initial[branchKey] || selectedInitial
            : selectedInitial;
        const finalSelectedId =
          branchView === "todas"
            ? inventoryMovementByBranch.final[branchKey] || selectedFinal
            : selectedFinal;
        const inventarioInicial = sumInventoryByMovement(
          initialDetails,
          initialSelectedId,
          Number(row.inventarioInicial ?? 0),
        );
        const inventarioFinal = sumInventoryByMovement(
          finalDetails,
          finalSelectedId,
          Number(row.inventarioFinal ?? 0),
        );
        const teorico =
          inventarioInicial +
          Number(row.compras ?? 0) +
          Number(row.remisionPositiva ?? 0) -
          Number(row.remisionNegativa ?? 0) -
          Number(row.ventas ?? 0) +
          Number(row.ajustePositivo ?? 0) -
          Number(row.ajusteNegativo ?? 0);
        const diferencia = inventarioFinal - teorico;

        return {
          ...row,
          inventarioInicial,
          inventarioFinal,
          teorico,
          diferencia,
        };
      })
      .filter((row): row is ControlStockRow => row !== null)
      .filter((row) => {
        if (differenceFilter === "con") {
          return row.diferencia !== 0;
        }
        if (differenceFilter === "sin") {
          return row.diferencia === 0;
        }
        if (differenceFilter === "positivas") {
          return row.diferencia > 0;
        }
        if (differenceFilter === "negativas") {
          return row.diferencia < 0;
        }
        return true;
      });
    if (branchView !== "todas") {
      return mappedRows;
    }

    const grouped = new Map<string, ControlStockRow>();
    const mergeDetails = (
      target: ControlStockRow,
      source: ControlStockRow,
      key: keyof NonNullable<ControlStockRow["__details"]>,
    ) => {
      const targetDetails = target.__details?.[key] ?? [];
      const sourceDetails = source.__details?.[key] ?? [];
      if (!Array.isArray(sourceDetails) || sourceDetails.length === 0) {
        return;
      }
      if (!target.__details) {
        target.__details = {};
      }
      target.__details[key] = [...targetDetails, ...sourceDetails];
    };

    mappedRows.forEach((row) => {
      if (row.__isTotal || row.__isGrandTotal) {
        return;
      }
      const idKey = String(row.idArticulo || row.articulo || "").trim();
      if (!idKey) {
        return;
      }
      const current = grouped.get(idKey);
      if (!current) {
        grouped.set(idKey, {
          ...row,
          sucursal: "Todas las sucursales",
          server: row.server ?? "GLOBAL",
          __isCollectionHeader: false,
          __isCollectionSubtotal: false,
        });
        return;
      }
      const quantityWeight =
        Number(row.inventarioInicial ?? 0) +
        Number(row.compras ?? 0) +
        Number(row.remisionPositiva ?? 0) +
        Number(row.remisionNegativa ?? 0) +
        Number(row.ventas ?? 0) +
        Number(row.ajustePositivo ?? 0) +
        Number(row.ajusteNegativo ?? 0) +
        Number(row.inventarioFinal ?? 0);
      const currentWeight =
        Number(current.inventarioInicial ?? 0) +
        Number(current.compras ?? 0) +
        Number(current.remisionPositiva ?? 0) +
        Number(current.remisionNegativa ?? 0) +
        Number(current.ventas ?? 0) +
        Number(current.ajustePositivo ?? 0) +
        Number(current.ajusteNegativo ?? 0) +
        Number(current.inventarioFinal ?? 0);
      const nextTotalWeight = currentWeight + quantityWeight;
      const currentCost = Number(current.valorUnitario ?? 0);
      const rowCost = Number(row.valorUnitario ?? 0);
      current.valorUnitario =
        nextTotalWeight > 0
          ? (currentCost * currentWeight + rowCost * quantityWeight) / nextTotalWeight
          : currentCost || rowCost || 0;
      current.inventarioInicial += Number(row.inventarioInicial ?? 0);
      current.compras += Number(row.compras ?? 0);
      current.remisionPositiva += Number(row.remisionPositiva ?? 0);
      current.remisionNegativa += Number(row.remisionNegativa ?? 0);
      current.ventas += Number(row.ventas ?? 0);
      current.ajustePositivo += Number(row.ajustePositivo ?? 0);
      current.ajusteNegativo += Number(row.ajusteNegativo ?? 0);
      current.inventarioFinal += Number(row.inventarioFinal ?? 0);
      current.teorico += Number(row.teorico ?? 0);
      current.diferencia += Number(row.diferencia ?? 0);
      mergeDetails(current, row, "inventarioInicial");
      mergeDetails(current, row, "inventarioFinal");
      mergeDetails(current, row, "compras");
      mergeDetails(current, row, "remisionPositiva");
      mergeDetails(current, row, "remisionNegativa");
      mergeDetails(current, row, "ventas");
      mergeDetails(current, row, "ajustePositivo");
      mergeDetails(current, row, "ajusteNegativo");
    });

    return Array.from(grouped.values());
  }, [
    baseFilteredData,
    inventoryMovementFilter,
    inventoryMovementByBranch,
    branchView,
    differenceFilter,
  ]);

  const hasAmountMode = metricMode !== "cantidad";
  const canUseBranchSummaryTab = hasAmountMode && branchFilter.length === 0;

  useEffect(() => {
    const isRemovedTab =
      activeStockTab === "stock-por-familia-linea" ||
      activeStockTab === "stock-por-sucursal";
    if (isRemovedTab) {
      setActiveStockTab("stock-por-articulo");
    }
  }, [activeStockTab]);

  const familyLineResults = useMemo<FamilyLineResultRow[]>(
    () => buildFamilyLineResults(filteredData),
    [filteredData],
  );

  const familyLineColumns = useMemo(() => buildFamilyLineColumns(), []);

  const branchResults = useMemo<BranchResultRow[]>(
    () => buildBranchResults(filteredData),
    [filteredData],
  );

  const branchColumns = useMemo(() => buildBranchColumns(), []);

  const movementDateColumns = useMemo(
    () => buildMovementDateColumns(dateRange.startDate, dateRange.endDate),
    [dateRange.startDate, dateRange.endDate],
  );

  const movementRowsByDate = useMemo<MovementByDateRow[]>(
    () => buildMovementRowsByDate(filteredData, movementDateMetric, dateRange.endDate),
    [dateRange.endDate, filteredData, movementDateMetric],
  );

  const movementDateAvailability = useMemo<Record<MovementDateMetric, boolean>>(
    () => buildMovementDateAvailability(filteredData),
    [filteredData],
  );

  const togglePdfMetric = useCallback(
    (key: typeof PDF_METRIC_KEYS[number]) => {
      setPdfHiddenMetrics((prev) => {
        const visibleCount = PDF_METRIC_KEYS.filter((k) => !prev[k]).length;
        if (!prev[key] && visibleCount <= 1) {
          return prev;
        }
        return { ...prev, [key]: !prev[key] };
      });
    },
    [],
  );

  const pdfVisibleMetricCount = useMemo(
    () => PDF_METRIC_KEYS.filter((k) => !pdfHiddenMetrics[k]).length,
    [pdfHiddenMetrics],
  );

  const buildMovementRowsForMetric = useCallback(
    (metric: MovementDateMetric) =>
      buildMovementRowsForMetricSelector(filteredData, metric, dateRange.endDate),
    [dateRange.endDate, filteredData],
  );

  useEffect(() => {
    if (movementDateAvailability[movementDateMetric]) {
      return;
    }
    const firstAvailable = MOVEMENT_DATE_METRIC_OPTIONS.find(
      (option) => movementDateAvailability[option.value],
    );
    if (firstAvailable) {
      setMovementDateMetric(firstAvailable.value);
    }
  }, [movementDateAvailability, movementDateMetric]);

  const handleExportExcel = (
    tab?: "stock-por-articulo" | "movimientos-por-fecha" | "stock-por-familia-linea" | "all",
  ) => {
    exportControlStockExcel(
      {
        filteredData,
        familyLineResults,
        movementDateAvailability,
        movementDateColumns,
        buildMovementRowsForMetric,
        metricMode,
        activeStockTab,
        hasAmountMode,
      },
      tab,
    );
  };

  const handleExportPdf = (
    tab?: "stock-por-articulo" | "movimientos-por-fecha" | "stock-por-familia-linea" | "all",
  ) => {
    exportControlStockPdf(
      {
        filteredData,
        displayData,
        familyLineResults,
        movementDateAvailability,
        movementDateColumns,
        buildMovementRowsForMetric,
        metricMode,
        activeStockTab,
        hasAmountMode,
        pdfHiddenMetrics,
        pdfHideCosto,
        logoDataUrl,
        serverFilter,
        branchFilter,
        controlMode,
        differenceFilter,
        dateSelection,
        familyFilter,
        lineFilter,
        familyLabelMap,
        lineLabelMap,
        columnHasData,
        previousDateLabel,
        finalDateLabel,
        movementDateLabel,
      },
      tab,
    );
  };

  const columnHasData = useMemo(() => {
    const map: Record<string, boolean> = {};
    METRIC_FIELDS.forEach((field) => {
      map[field] = false;
    });
    baseFilteredData.rows.forEach((row) => {
      METRIC_FIELDS.forEach((field) => {
        if (!map[field] && Number(row[field] ?? 0) !== 0) {
          map[field] = true;
        }
      });
    });
    return map;
  }, [baseFilteredData]);

  const inventoryHeaderMovementIds = useMemo(() => {
    const collectIds = (metric: "inventarioInicial" | "inventarioFinal") => {
      const ids = new Set<string>();
      baseFilteredData.rows.forEach((row) => {
        (row.__details?.[metric] ?? []).forEach((entry) => {
          if (entry.idMovimiento === null || entry.idMovimiento === undefined) {
            return;
          }
          const value = String(entry.idMovimiento).trim();
          if (value.length > 0) {
            ids.add(value);
          }
        });
      });
      return Array.from(ids).sort((a, b) =>
        b.localeCompare(a, undefined, { numeric: true }),
      );
    };

    return {
      inventarioInicial: collectIds("inventarioInicial"),
      inventarioFinal: collectIds("inventarioFinal"),
    };
  }, [baseFilteredData]);

  const inventoryHeaderMovementGroups = useMemo(() => {
    const buildGroups = (metric: "inventarioInicial" | "inventarioFinal") => {
      const map = new Map<string, Set<string>>();
      baseFilteredData.rows.forEach((row) => {
        const branch = String(row.sucursal || "Sin sucursal").trim();
        if (!map.has(branch)) {
          map.set(branch, new Set());
        }
        (row.__details?.[metric] ?? []).forEach((entry) => {
          if (entry.idMovimiento === null || entry.idMovimiento === undefined) {
            return;
          }
          const value = String(entry.idMovimiento).trim();
          if (value.length > 0) {
            map.get(branch)?.add(value);
          }
        });
      });
      const result: Array<{ branch: string; ids: string[] }> = [];
      map.forEach((ids, branch) => {
        result.push({
          branch,
          ids: Array.from(ids).sort((a, b) =>
            b.localeCompare(a, undefined, { numeric: true }),
          ),
        });
      });
      return result;
    };
    return {
      inventarioInicial: buildGroups("inventarioInicial"),
      inventarioFinal: buildGroups("inventarioFinal"),
    };
  }, [baseFilteredData]);

  const inventoryHeaderGroupMap = useMemo(() => {
    const build = (
      groups: Array<{ branch: string; ids: string[] }>,
    ): Record<string, string[]> => {
      const map: Record<string, string[]> = {};
      groups.forEach((group) => {
        const key = normalizeBranchKey(group.branch);
        map[key] = group.ids;
      });
      return map;
    };
    return {
      initial: build(inventoryHeaderMovementGroups.inventarioInicial),
      final: build(inventoryHeaderMovementGroups.inventarioFinal),
    };
  }, [inventoryHeaderMovementGroups]);

  useEffect(() => {
    if (branchView === "todas") {
      setInventoryMovementFilter({ initial: "", final: "" });
      return;
    }
    const key = normalizeBranchKey(branchView);
    const initialIds = inventoryHeaderGroupMap.initial[key] ?? [];
    const finalIds = inventoryHeaderGroupMap.final[key] ?? [];
    const nextInitial =
      inventoryMovementByBranch.initial[key] ??
      (initialIds.length > 0 ? initialIds[0] : "");
    const nextFinal =
      inventoryMovementByBranch.final[key] ??
      (finalIds.length > 0 ? finalIds[0] : "");
    setInventoryMovementFilter({
      initial: nextInitial,
      final: nextFinal,
    });
  }, [branchView, inventoryHeaderGroupMap, inventoryMovementByBranch]);

  const pickLatestId = (ids: string[]) => {
    if (!ids || ids.length === 0) return "";
    return ids
      .slice()
      .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))[0];
  };

  const hasInitialOverrides =
    Object.values(inventoryMovementByBranch.initial).filter(Boolean).length > 0;
  const hasFinalOverrides =
    Object.values(inventoryMovementByBranch.final).filter(Boolean).length > 0;

  const getIdmovLabel = (kind: "initial" | "final") => {
    if (branchView === "todas" && branchFilter.length > 1) {
      const hasOverrides = kind === "initial" ? hasInitialOverrides : hasFinalOverrides;
      return hasOverrides ? "Personalizado" : "Todos (sumar)";
    }
    const current =
      kind === "initial" ? inventoryMovementFilter.initial : inventoryMovementFilter.final;
    return current ? `IDMOV: ${current}` : "Todos (sumar)";
  };

  useEffect(() => {
    if (branchView !== "todas" || branchFilter.length <= 1) {
      return;
    }
    setInventoryMovementByBranch((prev) => {
      const nextInitial = { ...prev.initial };
      const nextFinal = { ...prev.final };
      branchFilter.forEach((branch) => {
        const key = normalizeBranchKey(branch);
        const initialIds = inventoryHeaderGroupMap.initial[key] ?? [];
        const finalIds = inventoryHeaderGroupMap.final[key] ?? [];
        if (initialIds.length > 1 && !nextInitial[key]) {
          nextInitial[key] = pickLatestId(initialIds);
        }
        if (finalIds.length > 1 && !nextFinal[key]) {
          nextFinal[key] = pickLatestId(finalIds);
        }
        if (initialIds.length === 1) {
          nextInitial[key] = initialIds[0];
        }
        if (finalIds.length === 1) {
          nextFinal[key] = finalIds[0];
        }
      });
      return { initial: nextInitial, final: nextFinal };
    });
  }, [branchView, branchFilter, inventoryHeaderGroupMap]);

  useEffect(() => {
    if (branchView === "todas") {
      setInventoryMovementFilter({ initial: "", final: "" });
      return;
    }
    setInventoryMovementFilter({
      initial: inventoryHeaderMovementIds.inventarioInicial[0] ?? "",
      final: inventoryHeaderMovementIds.inventarioFinal[0] ?? "",
    });
  }, [inventoryHeaderMovementIds, branchView]);

  useEffect(() => {
    if (!ENABLE_IDMOV_DRIVER_GUIDE) {
      if (idmovGuideTimeoutRef.current) {
        window.clearTimeout(idmovGuideTimeoutRef.current);
        idmovGuideTimeoutRef.current = null;
      }
      idmovGuidePendingRef.current = false;
      forceCloseDriver();
      return;
    }
    if (idmovGuideShownRef.current) {
      return;
    }
    const hasMultipleInitial = inventoryHeaderMovementIds.inventarioInicial.length > 1;
    const hasMultipleFinal = inventoryHeaderMovementIds.inventarioFinal.length > 1;
    if (!hasMultipleInitial && !hasMultipleFinal) {
      return;
    }

    const steps: DriveStep[] = [];
    if (hasMultipleInitial) {
      steps.push({
        element: "#idmov-inicial-select",
        popover: {
          title: "Seleccione el Inventario Inicial",
          description:
            "Hay más de una opción disponible. Elegí el IDMOV que corresponda.",
          side: "bottom",
          align: "start",
        },
      });
    }
    if (hasMultipleFinal) {
      steps.push({
        element: "#idmov-final-select",
        popover: {
          title: "Seleccione el Inventario Final",
          description:
            "Hay más de una opción disponible. Elegí el IDMOV que corresponda.",
          side: "bottom",
          align: "start",
        },
      });
    }
    const startGuide = () => {
      const validSteps = steps.filter((step) =>
        typeof step.element === "string"
          ? document.querySelector(step.element)
          : step.element,
      );
      if (validSteps.length === 0) {
        if (idmovGuideRetryRef.current < 4) {
          idmovGuideRetryRef.current += 1;
          window.setTimeout(() => {
            setIdmovGuideTick((prev) => prev + 1);
          }, 250);
        }
        return;
      }

      const driverInstance = driver({
        allowClose: true,
        showProgress: false,
        nextBtnText: "Siguiente",
        prevBtnText: "Atrás",
        doneBtnText: "Listo",
        onCloseClick: () => forceCloseDriver(),
        onDoneClick: () => forceCloseDriver(),
        onDestroyed: () => {
          idmovGuideShownRef.current = true;
          idmovDriverRef.current = null;
        },
      } as any);
      idmovGuideShownRef.current = false;
      idmovUserTouchedRef.current = false;
      idmovDriverRef.current = driverInstance;
      idmovGuideRetryRef.current = 0;
      setTimeout(() => {
        const closeBtn = document.querySelector(
          ".driver-popover-close-btn",
        ) as HTMLButtonElement | null;
        if (closeBtn) {
          closeBtn.addEventListener("click", () => {
            forceCloseDriver();
          });
        }
        const doneBtn = document.querySelector(
          ".driver-popover-done-btn",
        ) as HTMLButtonElement | null;
        if (doneBtn) {
          doneBtn.addEventListener("click", () => {
            forceCloseDriver();
          });
        }
      }, 0);
      driverInstance.setSteps(validSteps);
      driverInstance.drive();

      return () => {
        driverInstance.destroy();
      };
    };

    if (filtersOpen) {
      if (!idmovGuidePendingRef.current) {
        idmovGuidePendingRef.current = true;
        setFiltersOpen(false);
        if (idmovGuideTimeoutRef.current) {
          window.clearTimeout(idmovGuideTimeoutRef.current);
        }
        idmovGuideTimeoutRef.current = window.setTimeout(() => {
          startGuide();
          idmovGuidePendingRef.current = false;
          idmovGuideTimeoutRef.current = null;
        }, 350);
      }
      return;
    }

    const cleanup = startGuide();
    return () => {
      if (cleanup) cleanup();
      if (idmovGuideTimeoutRef.current) {
        window.clearTimeout(idmovGuideTimeoutRef.current);
        idmovGuideTimeoutRef.current = null;
      }
      idmovGuidePendingRef.current = false;
    };
  }, [
    inventoryHeaderMovementIds.inventarioInicial.length,
    inventoryHeaderMovementIds.inventarioFinal.length,
    filtersOpen,
    idmovGuideTick,
    ENABLE_IDMOV_DRIVER_GUIDE,
    forceCloseDriver,
  ]);

  useEffect(() => {
    if (!idmovUserTouchedRef.current) {
      return;
    }
    forceCloseDriver();
  }, [inventoryMovementFilter.initial, inventoryMovementFilter.final, forceCloseDriver]);

  useEffect(() => {
    if (!ENABLE_IDMOV_DRIVER_GUIDE) {
      return;
    }
    const onDriverClick = (event: MouseEvent) => {
      if (!idmovDriverRef.current) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }
      if (
        target.closest(".driver-popover-close-btn") ||
        target.closest(".driver-popover-done-btn") ||
        target.closest(".driver-popover-next-btn") ||
        target.closest(".driver-popover-prev-btn")
      ) {
        forceCloseDriver();
      }
    };
    const onDriverKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      forceCloseDriver();
    };
    document.addEventListener("click", onDriverClick, true);
    document.addEventListener("keydown", onDriverKey, true);
    return () => {
      document.removeEventListener("click", onDriverClick, true);
      document.removeEventListener("keydown", onDriverKey, true);
    };
  }, [ENABLE_IDMOV_DRIVER_GUIDE, forceCloseDriver]);

  const summaryCards = useMemo(
    () => buildSummaryCards(filteredData),
    [filteredData],
  );

  const detailRowsSource = useMemo(() => {
    if (!detailArticleTarget) {
      return filteredData;
    }
    const targetId = detailArticleTarget.idArticulo.trim();
    if (!targetId) {
      return filteredData;
    }
    return filteredData.filter(
      (row) => String(row.idArticulo ?? "").trim() === targetId,
    );
  }, [filteredData, detailArticleTarget]);

  const detailTable = useMemo(
    () => buildDetailTable(detailRowsSource, detailsMetric as DetailMetric),
    [detailRowsSource, detailsMetric],
  );

  const detailTableAllRows = useMemo(
    () => buildDetailTable(filteredData, detailsMetric as DetailMetric).rows,
    [filteredData, detailsMetric],
  );

  const detailMetricAvailability = useMemo<Record<DetailMetric, boolean>>(() => {
    return DETAIL_METRIC_OPTIONS.reduce<Record<DetailMetric, boolean>>((acc, option) => {
      acc[option.value] =
        buildDetailTable(detailRowsSource, option.value).rows.length > 0;
      return acc;
    }, {} as Record<DetailMetric, boolean>);
  }, [detailRowsSource]);

  useEffect(() => {
    const currentMetric = detailsMetric as DetailMetric;
    if (detailMetricAvailability[currentMetric]) {
      return;
    }
    const firstAvailable = DETAIL_METRIC_OPTIONS.find(
      (option) => detailMetricAvailability[option.value],
    );
    if (firstAvailable) {
      setDetailsMetric(firstAvailable.value);
    }
  }, [detailMetricAvailability, detailsMetric]);

  const differenceChartRows = useMemo(() => {
    const safeNumber = (value: unknown) => {
      const num = Number(value ?? 0);
      return Number.isFinite(num) ? num : 0;
    };

    const grouped = filteredData.reduce<Record<string, {
      label: string;
      diferencia: number;
      teorico: number;
      inventarioFinal: number;
    }>>((acc, row) => {
      const rawFamilia =
        row.familia ??
        row.__familiaId ??
        "Sin familia";
      const label = String(rawFamilia || "Sin familia").trim() || "Sin familia";
      if (!acc[label]) {
        acc[label] = {
          label,
          diferencia: 0,
          teorico: 0,
          inventarioFinal: 0,
        };
      }
      acc[label].diferencia += safeNumber(row.diferencia);
      acc[label].teorico += safeNumber(row.teorico);
      acc[label].inventarioFinal += safeNumber(row.inventarioFinal);
      return acc;
    }, {});

    return Object.values(grouped)
      .filter((row) => row.diferencia !== 0 || row.teorico !== 0 || row.inventarioFinal !== 0)
      .sort((a, b) => Math.abs(b.diferencia) - Math.abs(a.diferencia));
  }, [filteredData]);

  useEffect(() => {
    setDifferenceChartPage(1);
  }, [differenceChartRows.length]);

  const differenceChartPageCount = useMemo(() => {
    return Math.max(1, Math.ceil(differenceChartRows.length / differenceChartPageSize));
  }, [differenceChartRows.length]);

  const differenceChartPagedRows = useMemo(() => {
    const start = (differenceChartPage - 1) * differenceChartPageSize;
    return differenceChartRows.slice(start, start + differenceChartPageSize);
  }, [differenceChartPage, differenceChartRows]);

  const differenceChartOption = useMemo(() => {
    const labels = differenceChartPagedRows.map((row) => row.label);
    return {
      color: ["#f06548", "#405189", "#0ab39c"],
      grid: { left: 40, right: 20, top: 40, bottom: 90 },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: Array<{ seriesName: string; value: number; axisValue: string }>) => {
          if (!params || params.length === 0) return "";
          const label = params[0].axisValue ?? "";
          const lines = params
            .map((item) => `${item.seriesName}: ${formatNumber(item.value)}`)
            .join("<br/>");
          return `<strong>${label}</strong><br/>${lines}`;
        },
      },
      legend: { top: 8, left: 0 },
      xAxis: {
        type: "category",
        data: labels,
        axisLabel: {
          interval: 0,
          rotate: 45,
          formatter: (value: string) =>
            value.length > 18 ? `${value.slice(0, 16)}...` : value,
        },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          formatter: (value: number) => formatNumber(value),
        },
      },
      series: [
        {
          name: "Diferencia",
          type: "bar",
          data: differenceChartPagedRows.map((row) => row.diferencia),
        },
        {
          name: "Teórico",
          type: "line",
          smooth: true,
          data: differenceChartPagedRows.map((row) => row.teorico),
        },
        {
          name: "Inventario final",
          type: "line",
          smooth: true,
          data: differenceChartPagedRows.map((row) => row.inventarioFinal),
        },
      ],
    };
  }, [differenceChartPagedRows]);

  const getAmountValue = useCallback(
    (row: ControlStockRow, field: keyof ControlStockRow) => {
      if (row.__isTotal && row.__amountTotals) {
        return row.__amountTotals[field] ?? 0;
      }
      return Number(row[field] ?? 0) * row.valorUnitario;
    },
    [],
  );

  const renderMetricCell = useCallback(
    (
      row: ControlStockRow,
      field: keyof ControlStockRow,
      options: { signed?: boolean; className?: string } = {},
    ) => {
      if (row.__isCollectionHeader) {
        return <span className="text-muted"> </span>;
      }
      const qtyValue = Number(row[field] ?? 0);
      const amountValue = getAmountValue(row, field);
      const signed = options.signed ?? false;
      const qtyText = signed
        ? qtyValue > 0
          ? `+${formatNumber(qtyValue)}`
          : formatNumber(qtyValue)
        : formatNumber(qtyValue);
      const amountText = signed
        ? amountValue > 0
          ? `+${formatCurrency(amountValue)}`
          : formatCurrency(amountValue)
        : formatCurrency(amountValue);
      const baseClass = [
        "d-block",
        "text-end",
        row.__isTotal ? "fw-semibold" : "",
        options.className ?? "",
      ]
        .filter(Boolean)
        .join(" ");
      const renderDash = qtyValue === 0 && amountValue === 0;
      if (metricMode === "cantidad") {
        return (
          <span className={baseClass}>{renderDash ? "—" : qtyText}</span>
        );
      }
      if (metricMode === "monto") {
        return (
          <span className={baseClass}>{renderDash ? "—" : amountText}</span>
        );
      }

      if (metricMode === "ambos" && renderDash) {
        return <span className={baseClass}>—</span>;
      }

      return (
        <div className={`${baseClass} lh-sm`}>
          <div>{qtyText}</div>
          <small className="text-muted">{amountText}</small>
        </div>
      );
    },
    [getAmountValue, metricMode],
  );

  const handleViewMovementsFromRow = useCallback((row: ControlStockRow) => {
    if (row.__isCollectionHeader || row.__isTotal || row.__isCollectionSubtotal) {
      return;
    }
    const targetId = String(row.idArticulo ?? "").trim();
    if (!targetId) {
      return;
    }
    setDetailArticleTarget({
      idArticulo: targetId,
      articulo: String(row.articulo ?? "").trim(),
    });
    setIsDetailsModalOpen(true);
  }, []);

  const handleCloseDetailsModal = useCallback(() => {
    setIsDetailsModalOpen(false);
    setDetailArticleTarget(null);
  }, []);

  const columns = useMemo(
    () =>
      buildStockColumns({
        previousDateLabel,
        movementDateLabel,
        finalDateLabel,
        columnHasData,
        inventoryHeaderMovementIds,
        inventoryMovementFilter,
        onInventoryMovementFilterChange: handleInventoryMovementFilterChange,
        metricMode,
        getAmountValue,
        renderMetricCell,
        onRowViewMovements: handleViewMovementsFromRow,
      }),
    [
      previousDateLabel,
      movementDateLabel,
      finalDateLabel,
      columnHasData,
      inventoryHeaderMovementIds,
      inventoryMovementFilter,
      handleInventoryMovementFilterChange,
      metricMode,
      getAmountValue,
      renderMetricCell,
      handleViewMovementsFromRow,
    ],
  );

  const totalsRow = useMemo<ControlStockRow | null>(
    () => buildTotalsRow(filteredData, metricMode),
    [filteredData, metricMode],
  );

  const agentSnapshotRows = useMemo(() => {
    const selectedInitial = inventoryMovementFilter.initial.trim();
    const selectedFinal = inventoryMovementFilter.final.trim();

    const sumInventoryByMovement = (
      details: MovementEntry[] | undefined,
      selectedId: string,
      fallback: number,
    ) => {
      if (!selectedId) {
        return fallback;
      }
      if (!Array.isArray(details) || details.length === 0) {
        return 0;
      }
      return details.reduce((acc, entry) => {
        if (entry.idMovimiento == null) {
          return acc;
        }
        return String(entry.idMovimiento).trim() === selectedId
          ? acc + Number(entry.cantidad ?? 0)
          : acc;
      }, 0);
    };

    return baseFilteredData.rows.map((row) => {
      const initialDetails = row.__details?.inventarioInicial ?? [];
      const finalDetails = row.__details?.inventarioFinal ?? [];
      const branchKey = normalizeBranchKey(row.sucursal);
      const initialSelectedId =
        branchView === "todas"
          ? inventoryMovementByBranch.initial[branchKey] || selectedInitial
          : selectedInitial;
      const inventarioInicial = sumInventoryByMovement(
        initialDetails,
        initialSelectedId,
        Number(row.inventarioInicial ?? 0),
      );
      const finalSelectedId =
        branchView === "todas"
          ? inventoryMovementByBranch.final[branchKey] || selectedFinal
          : selectedFinal;
      const inventarioFinal = sumInventoryByMovement(
        finalDetails,
        finalSelectedId,
        Number(row.inventarioFinal ?? 0),
      );
      const teorico =
        inventarioInicial +
        Number(row.compras ?? 0) +
        Number(row.remisionPositiva ?? 0) -
        Number(row.remisionNegativa ?? 0) -
        Number(row.ventas ?? 0) +
        Number(row.ajustePositivo ?? 0) -
        Number(row.ajusteNegativo ?? 0);
      const diferencia = inventarioFinal - teorico;

      return {
        ...row,
        inventarioInicial,
        inventarioFinal,
        teorico,
        diferencia,
      };
    });
  }, [
    baseFilteredData,
    inventoryMovementFilter.final,
    inventoryMovementFilter.initial,
    inventoryMovementByBranch,
    branchView,
  ]);

  const agentSnapshotReady = !recordsLoading;

  const agentSnapshotTableRows = useMemo(() => {
    const rows = agentSnapshotRows.filter(
      (row) =>
        !row.__isTotal &&
        !row.__isGrandTotal &&
        !row.__isCollectionHeader &&
        !row.__isCollectionSubtotal,
    );
    const limit = 200;
    return {
      total: rows.length,
      limit,
      rows: rows.slice(0, limit).map((row) => ({
        idArticulo: row.idArticulo,
        articulo: row.articulo,
        familia: row.familia ?? null,
        linea: row.linea ?? null,
        unidad: row.unidadMedida ?? null,
        costoUnitario: row.valorUnitario ?? null,
        inventarioInicial: row.inventarioInicial ?? 0,
        compras: row.compras ?? 0,
        remisionPositiva: row.remisionPositiva ?? 0,
        remisionNegativa: row.remisionNegativa ?? 0,
        ventas: row.ventas ?? 0,
        ajustePositivo: row.ajustePositivo ?? 0,
        ajusteNegativo: row.ajusteNegativo ?? 0,
        inventarioFinal: row.inventarioFinal ?? 0,
        teorico: row.teorico ?? 0,
        diferencia: row.diferencia ?? 0,
      })),
    };
  }, [agentSnapshotRows]);

  const agentSnapshotFamilyLineRows = useMemo(() => {
    const limit = 200;
    return {
      total: familyLineResults.length,
      limit,
      rows: familyLineResults.slice(0, limit).map((row) => ({
        familia: row.familia,
        linea: row.linea,
        inventarioInicial: row.inventarioInicial,
        compras: row.compras,
        remisionPositiva: row.remisionPositiva,
        remisionNegativa: row.remisionNegativa,
        ventas: row.ventas,
        ajustePositivo: row.ajustePositivo,
        ajusteNegativo: row.ajusteNegativo,
        inventarioFinal: row.inventarioFinal,
        teorico: row.teorico,
        diferencia: row.diferencia,
      })),
    };
  }, [familyLineResults]);

  const agentSnapshotBranchRows = useMemo(() => {
    const limit = 200;
    return {
      total: branchResults.length,
      limit,
      rows: branchResults.slice(0, limit).map((row) => ({
        sucursal: row.sucursal,
        inventarioInicial: row.inventarioInicial,
        compras: row.compras,
        remisionPositiva: row.remisionPositiva,
        remisionNegativa: row.remisionNegativa,
        ventas: row.ventas,
        ajustePositivo: row.ajustePositivo,
        ajusteNegativo: row.ajusteNegativo,
        inventarioFinal: row.inventarioFinal,
        teorico: row.teorico,
        diferencia: row.diferencia,
      })),
    };
  }, [branchResults]);

  const agentSnapshotMovementRows = useMemo(() => {
    const limit = 200;
    return {
      total: movementRowsByDate.length,
      limit,
      metric: movementDateMetric,
      rows: movementRowsByDate.slice(0, limit).map((row) => ({
        articulo: row.articulo,
        idArticulo: row.idArticulo,
        unidad: row.unidadMedida,
        totalCantidad: row.totalCantidad,
        totalMonto: row.totalMonto,
        porFecha: row.porFecha,
      })),
      columnas: movementDateColumns,
    };
  }, [movementRowsByDate, movementDateMetric, movementDateColumns]);

  const agentSnapshotPayload = useMemo(() => {
    const rows = agentSnapshotRows.filter(
      (row) =>
        !row.__isTotal &&
        !row.__isGrandTotal &&
        !row.__isCollectionHeader &&
        !row.__isCollectionSubtotal,
    );
    const totalDiferencia = rows.reduce((acc, row) => acc + (row.diferencia || 0), 0);
    const topNegativos = [...rows]
      .filter((row) => row.diferencia < 0)
      .sort((a, b) => a.diferencia - b.diferencia)
      .slice(0, 5)
      .map((row) => ({ articulo: row.articulo, diferencia: row.diferencia }));
    const topPositivos = [...rows]
      .filter((row) => row.diferencia > 0)
      .sort((a, b) => b.diferencia - a.diferencia)
      .slice(0, 5)
      .map((row) => ({ articulo: row.articulo, diferencia: row.diferencia }));

    return {
	        filtros: {
	          server: serverFilter,
	          sucursal: branchFilter,
          sucursalVista: branchView,
          controlMode,
          differenceFilter,
          metricMode,
          groupViewMode,
      searchTerm,
      semanticTokens,
      searchMatchMode: baseFilteredData.searchMode,
          searchTokens: baseFilteredData.searchTokens,
          searchExactCount: baseFilteredData.searchExactCount,
          searchApproxCount: baseFilteredData.searchApproxCount,
          collectionFilter,
          inventoryMovementInitial: inventoryMovementFilter.initial,
          inventoryMovementFinal: inventoryMovementFilter.final,
          dateSelection,
          pdfHideCosto,
          pdfHiddenMetrics,
        },
      datos: {
        meta: {
          cargando: recordsLoading,
        },
        resumen: {
          totalArticulos: rows.length,
          totalDiferencia,
          topNegativos,
          topPositivos,
          totalVisibles: filteredData.length,
        },
        tarjetasResumen: summaryCards,
        tabla: agentSnapshotTableRows,
        tablas: {
          stockPorArticulo: agentSnapshotTableRows,
          movimientosPorFecha: agentSnapshotMovementRows,
          familiaLinea: agentSnapshotFamilyLineRows,
          sucursal: agentSnapshotBranchRows,
        },
        detallesMovimientos: {
          metric: detailsMetric,
          total: detailTable.rows.length,
          limit: 200,
          rows: detailTable.rows.slice(0, 200),
          totales: detailTable.totals,
        },
        ui: {
          colecciones: {
            permisos: {
              ver: canViewCollections,
              editar: canEditCollections,
              clonar: canCloneCollections,
            },
            items: collectionGroups.map((collection) => ({
              id: String(collection.id),
              nombre: collection.nombre,
              total_articulos: collection.articulos.size,
            })),
          },
          pestañas: [
            { id: "resumen", label: "Resumen", habilitada: true },
            { id: "stock-por-articulo", label: "Control de stock por artículo", habilitada: true },
            { id: "movimientos-por-fecha", label: "Movimientos por fecha", habilitada: true },
          ],
          pestanaActiva: activeStockTab,
          filtros: {
            servidores: serverOptions,
            sucursales: branchOptions,
            familias: familyOptions.map((item) => item.label),
            lineas: lineOptions.map((item) => item.label),
            colecciones: collectionGroups.map((collection) => ({
              id: String(collection.id),
              nombre: collection.nombre,
            })),
            idmovInventarioInicial: inventoryHeaderMovementIds.inventarioInicial,
            idmovInventarioFinal: inventoryHeaderMovementIds.inventarioFinal,
            vista: [
              { value: "sin", label: "Sin colección" },
              { value: "coleccion", label: "Por colección" },
            ],
            modoFecha: ["day", "week", "month", "range"],
            diferencia: ["todas", "con", "sin", "positivas", "negativas"],
            modoControl: ["recetas", "inventario"],
            modoMetricas: ["cantidad", "monto", "ambos"],
          },
          descargas: {
            excel: [
              { id: "stock-por-articulo", label: "Control de stock por artículo" },
              { id: "movimientos-por-fecha", label: "Movimientos por fecha" },
              { id: "stock-por-familia-linea", label: "Resultado por familia y línea" },
              { id: "all", label: "Todos" },
            ],
            pdf: [
              { id: "stock-por-articulo", label: "Control de stock por artículo" },
              { id: "movimientos-por-fecha", label: "Movimientos por fecha" },
              { id: "stock-por-familia-linea", label: "Resultado por familia y línea" },
              { id: "all", label: "Todos" },
            ],
            pdfOpciones: [
              { campo: "costo", label: "Ocultar columna COSTO MP U." },
              { campo: "inventarioInicial", label: "Ocultar columna INICIAL" },
              { campo: "compras", label: "Ocultar columna COMPRA" },
              { campo: "remisionPositiva", label: "Ocultar columna REM(+)" },
              { campo: "remisionNegativa", label: "Ocultar columna REM(-)" },
              { campo: "ventas", label: "Ocultar columna VENTA" },
              { campo: "ajusteNegativo", label: "Ocultar columna MERMA" },
              { campo: "teorico", label: "Ocultar columna TEORICO" },
              { campo: "inventarioFinal", label: "Ocultar columna FINAL" },
              { campo: "diferencia", label: "Ocultar columna DIF." },
            ],
          },
        },
      },
    };
  }, [
    filteredData,
    agentSnapshotRows,
    agentSnapshotTableRows,
    agentSnapshotFamilyLineRows,
    agentSnapshotBranchRows,
    agentSnapshotMovementRows,
    summaryCards,
    detailTable,
    detailsMetric,
    recordsLoading,
    serverFilter,
    branchFilter,
    controlMode,
    differenceFilter,
    metricMode,
    dateSelection,
    serverOptions,
    branchOptions,
    familyOptions,
    lineOptions,
    collectionGroups,
    inventoryHeaderMovementIds.inventarioInicial,
    inventoryHeaderMovementIds.inventarioFinal,
    inventoryMovementFilter.initial,
    inventoryMovementFilter.final,
    groupViewMode,
    searchTerm,
    collectionFilter,
    canViewCollections,
    canEditCollections,
    canCloneCollections,
    hasAmountMode,
    canUseBranchSummaryTab,
    activeStockTab,
  ]);

  const applyAgentFilters = useCallback(
    ({
      fecha,
      sucursal,
      familia,
      linea,
      servidor,
      differenceFilter: diff,
      controlMode: control,
      metricMode: metric,
      dateMode,
      week,
      month,
      rangeFrom,
      rangeTo,
      searchTerm: searchValue,
      semanticTokens: semanticValue,
      collectionFilter: collectionValue,
      inventoryMovementInitial,
      inventoryMovementFinal,
      groupViewMode: viewMode,
    }: {
      fecha?: string | null;
      sucursal?: string | string[] | null;
      familia?: string | null;
      linea?: string | null;
      servidor?: string | null;
      differenceFilter?: string | null;
      controlMode?: string | null;
      metricMode?: string | null;
      dateMode?: string | null;
      week?: string | null;
      month?: string | null;
      rangeFrom?: string | null;
      rangeTo?: string | null;
      searchTerm?: string | null;
      semanticTokens?: string[] | null;
      collectionFilter?: string | null;
      inventoryMovementInitial?: string | null;
      inventoryMovementFinal?: string | null;
      groupViewMode?: string | null;
    }) => {
      const debugEnabled =
        typeof window !== "undefined" && (window as any).__AGENTE_DEBUG__ === true;
      if (debugEnabled) {
        // eslint-disable-next-line no-console
        console.debug("[AgenteIA] applyAgentFilters", {
          fecha,
          dateMode,
          rangeFrom,
          rangeTo,
          week,
          month,
          sucursal,
          servidor,
          diferencia: diff,
          control,
          metric,
          searchValue,
          collectionValue,
        });
      }
      if (!servidor && !serverFilter) {
        const defaultServer = serverOptions.find((opt) => opt === "PARLA_SERVER");
        if (defaultServer) {
          setServerFilter(defaultServer);
        }
      }
      if (servidor) {
        const found = serverOptions.find(
          (opt) => opt.toLowerCase() === servidor.toLowerCase(),
        );
        if (found) {
          setServerFilter(found);
        }
      }
      if (fecha) {
        setDateSelection((prev) => ({
          ...prev,
          mode: "day",
          day: fecha,
          week: prev.week,
          month: prev.month,
          rangeFrom: prev.rangeFrom,
          rangeTo: prev.rangeTo,
        }));
      }
      if (dateMode && ["day", "week", "month", "range"].includes(dateMode)) {
        if (dateMode === "day" && !fecha) {
          return;
        }
        if (dateMode === "week" && !week) {
          return;
        }
        if (dateMode === "month" && !month) {
          return;
        }
        if (dateMode === "range" && !rangeFrom && !rangeTo) {
          return;
        }
        setDateSelection((prev) => ({
          ...prev,
          mode: dateMode as DateMode,
          day: dateMode === "day" && fecha ? fecha : prev.day,
          week: dateMode === "week" && week ? week : prev.week,
          month: dateMode === "month" && month ? month : prev.month,
          rangeFrom: dateMode === "range" && rangeFrom ? rangeFrom : prev.rangeFrom,
          rangeTo: dateMode === "range" && rangeTo ? rangeTo : prev.rangeTo,
        }));
      }
      if (sucursal) {
        if (Array.isArray(sucursal)) {
          if (sucursal.length === 0) {
            setBranchFilter([]);
          } else {
            setBranchFilter(
              sucursal.map((item) => String(item).trim()).filter(Boolean),
            );
          }
        } else {
        const normalized = String(sucursal).trim().toLowerCase();
        if (
          normalized === "todas" ||
          normalized === "todas las sucursales" ||
          normalized === "todas las sucursal"
        ) {
          setBranchFilter([]);
        } else {
        const found = branchOptions.find(
          (opt) => opt.toLowerCase() === String(sucursal).toLowerCase(),
        );
        if (found) {
          setBranchFilter([found]);
        } else {
          setBranchFilter([String(sucursal).toUpperCase()]);
        }
        }
        }
      }
      if (familia) {
        const found = familyOptions.find(
          (opt) => opt.label.toLowerCase() === familia.toLowerCase(),
        );
        if (found?.value) {
          setFamilyFilter(found.value);
        }
      }
      if (linea) {
        const found = lineOptions.find(
          (opt) => opt.label.toLowerCase() === linea.toLowerCase(),
        );
        if (found?.value) {
          setLineFilter(found.value);
        }
      }
      if (diff && ["todas", "con", "sin", "positivas", "negativas"].includes(diff)) {
        setDifferenceFilter(diff as any);
      }
      if (control && ["recetas", "inventario"].includes(control)) {
        setControlMode(control as any);
      }
      if (metric && ["cantidad", "monto", "ambos"].includes(metric)) {
        setMetricMode(metric as any);
      }
      if (typeof searchValue === "string") {
        setSearchTerm(searchValue);
      }
      if (Array.isArray(semanticValue)) {
        const cleaned = semanticValue
          .map((item) => String(item).trim())
          .filter(Boolean);
        setSemanticTokens(cleaned);
        if (cleaned.length > 0) {
          setSearchTerm("");
        }
      }
      if (Array.isArray(semanticValue)) {
        setSemanticTokens(semanticValue.map((item) => String(item).trim()).filter(Boolean));
      }
      if (collectionValue) {
        setCollectionFilter(collectionValue);
      }
      if (inventoryMovementInitial !== undefined) {
        handleInventoryMovementFilterChange("initial", inventoryMovementInitial || "");
      }
      if (inventoryMovementFinal !== undefined) {
        handleInventoryMovementFilterChange("final", inventoryMovementFinal || "");
      }
      if (viewMode && (viewMode === "sin" || viewMode === "coleccion")) {
        setGroupViewMode(viewMode);
      }
    },
    [
      branchOptions,
      familyOptions,
      lineOptions,
      serverOptions,
      serverFilter,
    ],
  );

  const displayData = useMemo(() => {
    if (groupViewMode === "coleccion") {
      const rowsByCollection = collectionGroups.map(() => [] as ControlStockRow[]);
      const unassigned: ControlStockRow[] = [];

      filteredData.forEach((row) => {
        const id = String(row.idArticulo ?? "").trim();
        if (!id) {
          unassigned.push(row);
          return;
        }
        let assigned = false;
        for (let index = 0; index < collectionGroups.length; index += 1) {
          if (collectionGroups[index].articulos.has(id)) {
            rowsByCollection[index].push(row);
            assigned = true;
            break;
          }
        }
        if (!assigned) {
          unassigned.push(row);
        }
      });

      const buildHeaderRow = (label: string, diffTotal: number): ControlStockRow => ({
        idArticulo: "",
        articulo: "",
        server: "",
        sucursal: "",
        fechaReferencia: "",
        valorUnitario: 1,
        unidadMedida: "",
        familia: null,
        linea: null,
        inventarioInicial: 0,
        compras: 0,
        remisionPositiva: 0,
        remisionNegativa: 0,
        ventas: 0,
        ajustePositivo: 0,
        ajusteNegativo: 0,
        teorico: 0,
        inventarioFinal: 0,
        diferencia: diffTotal,
        __isCollectionHeader: true,
        __collectionLabel: label,
        __collectionDiff: diffTotal,
        __familiaId: null,
        __lineaId: null,
      });

      const buildSubtotalRow = (
        label: string,
        rows: ControlStockRow[],
      ): ControlStockRow | null => {
        const subtotal = buildTotalsRow(rows, metricMode);
        if (!subtotal) return null;
        return {
          ...subtotal,
          __isGrandTotal: false,
          __isCollectionSubtotal: true,
          __totalLabel: `Subtotal ${label}`,
          __collectionLabel: label,
        };
      };

      const result: ControlStockRow[] = [];
      collectionGroups.forEach((collection, index) => {
        const rows = rowsByCollection[index];
        if (rows.length === 0) return;
        const diffTotal = rows.reduce((acc, row) => acc + Number(row.diferencia ?? 0), 0);
        result.push(buildHeaderRow(collection.nombre, diffTotal));
        result.push(...rows);
        const subtotal = buildSubtotalRow(collection.nombre, rows);
        if (subtotal) result.push(subtotal);
      });

      if (unassigned.length > 0 || collectionGroups.length === 0) {
        const label = "Sin colección";
        const diffTotal = unassigned.reduce((acc, row) => acc + Number(row.diferencia ?? 0), 0);
        result.push(buildHeaderRow(label, diffTotal));
        result.push(...unassigned);
        const subtotal = buildSubtotalRow(label, unassigned);
        if (subtotal) result.push(subtotal);
      }

      if (totalsRow) {
        result.push({
          ...totalsRow,
          __totalLabel: "Total general",
        });
      }
      return result;
    }
    if (totalsRow) {
      return [...filteredData, totalsRow];
    }
    return filteredData;
  }, [collectionGroups, filteredData, groupViewMode, metricMode, totalsRow]);
  const filtersDisabled = displayData.length === 0;
  const isSummaryDisabled = filteredData.length === 0;

  return (
    <div className="page-content">
      <Container fluid className="px-2 px-md-3">
        <style>{`
          .control-stock-main-table table th,
          .control-stock-main-table table td {
            vertical-align: top !important;
            padding-top: 0.42rem !important;
            padding-bottom: 0.42rem !important;
            padding-left: 0.55rem !important;
            padding-right: 0.55rem !important;
          }
          .control-stock-main-table table thead th {
            font-size: 0.925rem !important;
            line-height: 1.05 !important;
          }
          .control-stock-main-table table tbody td {
            font-size: 0.925rem !important;
            line-height: 1.06 !important;
          }
          .control-stock-main-table .table-empty-state-cell {
            padding-top: 8rem !important;
            padding-bottom: 8rem !important;
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }
          .control-stock-sticky-table thead th {
            position: sticky;
            top: 0;
            z-index: 9;
            background-color: var(--vz-secondary-bg, var(--vz-light));
          }
          .control-stock-sticky-table thead th:first-child {
            left: 0;
            z-index: 11;
            box-shadow: 1px 0 0 var(--vz-border-color);
          }
          .control-stock-sticky-table tbody td:first-child:not([colspan]) {
            position: sticky;
            left: 0;
            z-index: 6;
            background-color: var(--vz-body-bg);
            box-shadow: 1px 0 0 var(--vz-border-color);
          }
          .control-stock-sticky-table tbody tr.table-light td:first-child:not([colspan]),
          .control-stock-sticky-table tbody tr.table-warning-subtle td:first-child:not([colspan]),
          .control-stock-sticky-table tbody tr.table-info-subtle td:first-child:not([colspan]) {
            background-color: var(--vz-secondary-bg, var(--vz-light));
          }
        `}</style>
        <BreadCrumb title="Control de Stock" pageTitle="Informes" />

        <Card className="border-0 shadow-sm">
        <CardHeader className="bg-transparent">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <h4 className="mb-1">Análisis de stock</h4>
                <p className="text-muted mb-0">
                  Informe de análisis de cantidades en stock.
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2 ms-auto align-items-center">
                <Dropdown
                  isOpen={excelDropdownOpen}
                  toggle={() => setExcelDropdownOpen((prev) => !prev)}
                >
                  <DropdownToggle
                    color="dark"
                    className="btn-soft-dark"
                    caret
                    disabled={filteredData.length === 0}
                  >
                    <i className="ri-file-excel-2-line me-1"></i>
                    Excel
                  </DropdownToggle>
                  <DropdownMenu end>
                    <DropdownItem onClick={() => handleExportExcel("stock-por-articulo")}>
                      <i className="ri-file-list-3-line me-2"></i>
                      Control de stock por artículo
                    </DropdownItem>
                    <DropdownItem onClick={() => handleExportExcel("movimientos-por-fecha")}>
                      <i className="ri-calendar-event-line me-2"></i>
                      Movimientos por fecha
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => handleExportExcel("stock-por-familia-linea")}
                      disabled={!hasAmountMode}
                    >
                      <i className="ri-node-tree me-2"></i>
                      Resultado por familia y línea
                    </DropdownItem>
                    <DropdownItem onClick={() => handleExportExcel("all")}>
                      <i className="ri-file-copy-2-line me-2"></i>
                      Todos
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <Dropdown
                  isOpen={pdfDropdownOpen}
                  toggle={() => setPdfDropdownOpen((prev) => !prev)}
                >
                  <DropdownToggle
                    color="dark"
                    className="btn-soft-dark"
                    caret
                    disabled={filteredData.length === 0}
                  >
                    <i className="ri-file-pdf-2-line me-1"></i>
                    PDF
                  </DropdownToggle>
                  <DropdownMenu end>
                    <DropdownItem toggle={false}>
                      <div className="form-check m-0">
                        <Input
                          id="pdf-hide-costo"
                          type="checkbox"
                          className="form-check-input"
                          checked={pdfHideCosto}
                          onChange={() => setPdfHideCosto((prev) => !prev)}
                        />
                        <Label
                          className="form-check-label ms-2"
                          htmlFor="pdf-hide-costo"
                        >
                          Ocultar columna COSTO MP U.
                        </Label>
                      </div>
                    </DropdownItem>
                    <DropdownItem toggle={false}>
                      <div className="form-check m-0">
                        <Input
                          id="pdf-hide-invt-inic"
                          type="checkbox"
                          className="form-check-input"
                          checked={pdfHiddenMetrics.inventarioInicial}
                          disabled={
                            !pdfHiddenMetrics.inventarioInicial && pdfVisibleMetricCount === 1
                          }
                          onChange={() => togglePdfMetric("inventarioInicial")}
                        />
                        <Label
                          className="form-check-label ms-2"
                          htmlFor="pdf-hide-invt-inic"
                        >
                          Ocultar columna INICIAL
                        </Label>
                      </div>
                    </DropdownItem>
                    <DropdownItem toggle={false}>
                      <div className="form-check m-0">
                        <Input
                          id="pdf-hide-compr"
                          type="checkbox"
                          className="form-check-input"
                          checked={pdfHiddenMetrics.compras}
                          disabled={!pdfHiddenMetrics.compras && pdfVisibleMetricCount === 1}
                          onChange={() => togglePdfMetric("compras")}
                        />
                        <Label className="form-check-label ms-2" htmlFor="pdf-hide-compr">
                          Ocultar columna COMPRA
                        </Label>
                      </div>
                    </DropdownItem>
                    <DropdownItem toggle={false}>
                      <div className="form-check m-0">
                        <Input
                          id="pdf-hide-rem-pos"
                          type="checkbox"
                          className="form-check-input"
                          checked={pdfHiddenMetrics.remisionPositiva}
                          disabled={
                            !pdfHiddenMetrics.remisionPositiva && pdfVisibleMetricCount === 1
                          }
                          onChange={() => togglePdfMetric("remisionPositiva")}
                        />
                        <Label className="form-check-label ms-2" htmlFor="pdf-hide-rem-pos">
                          Ocultar columna REM(+)
                        </Label>
                      </div>
                    </DropdownItem>
                    <DropdownItem toggle={false}>
                      <div className="form-check m-0">
                        <Input
                          id="pdf-hide-rem-neg"
                          type="checkbox"
                          className="form-check-input"
                          checked={pdfHiddenMetrics.remisionNegativa}
                          disabled={
                            !pdfHiddenMetrics.remisionNegativa && pdfVisibleMetricCount === 1
                          }
                          onChange={() => togglePdfMetric("remisionNegativa")}
                        />
                        <Label className="form-check-label ms-2" htmlFor="pdf-hide-rem-neg">
                          Ocultar columna REM(-)
                        </Label>
                      </div>
                    </DropdownItem>
                    <DropdownItem toggle={false}>
                      <div className="form-check m-0">
                        <Input
                          id="pdf-hide-vent"
                          type="checkbox"
                          className="form-check-input"
                          checked={pdfHiddenMetrics.ventas}
                          disabled={!pdfHiddenMetrics.ventas && pdfVisibleMetricCount === 1}
                          onChange={() => togglePdfMetric("ventas")}
                        />
                        <Label className="form-check-label ms-2" htmlFor="pdf-hide-vent">
                          Ocultar columna VENTA
                        </Label>
                      </div>
                    </DropdownItem>
                    <DropdownItem toggle={false}>
                      <div className="form-check m-0">
                        <Input
                          id="pdf-hide-merma"
                          type="checkbox"
                          className="form-check-input"
                          checked={pdfHiddenMetrics.ajusteNegativo}
                          disabled={
                            !pdfHiddenMetrics.ajusteNegativo && pdfVisibleMetricCount === 1
                          }
                          onChange={() => togglePdfMetric("ajusteNegativo")}
                        />
                        <Label className="form-check-label ms-2" htmlFor="pdf-hide-merma">
                          Ocultar columna MERMA
                        </Label>
                      </div>
                    </DropdownItem>
                    <DropdownItem toggle={false}>
                      <div className="form-check m-0">
                        <Input
                          id="pdf-hide-teorico"
                          type="checkbox"
                          className="form-check-input"
                          checked={pdfHiddenMetrics.teorico}
                          disabled={!pdfHiddenMetrics.teorico && pdfVisibleMetricCount === 1}
                          onChange={() => togglePdfMetric("teorico")}
                        />
                        <Label className="form-check-label ms-2" htmlFor="pdf-hide-teorico">
                          Ocultar columna TEORICO
                        </Label>
                      </div>
                    </DropdownItem>
                    <DropdownItem toggle={false}>
                      <div className="form-check m-0">
                        <Input
                          id="pdf-hide-invt-finl"
                          type="checkbox"
                          className="form-check-input"
                          checked={pdfHiddenMetrics.inventarioFinal}
                          disabled={
                            !pdfHiddenMetrics.inventarioFinal && pdfVisibleMetricCount === 1
                          }
                          onChange={() => togglePdfMetric("inventarioFinal")}
                        />
                        <Label className="form-check-label ms-2" htmlFor="pdf-hide-invt-finl">
                          Ocultar columna FINAL
                        </Label>
                      </div>
                    </DropdownItem>
                    <DropdownItem toggle={false}>
                      <div className="form-check m-0">
                        <Input
                          id="pdf-hide-dif"
                          type="checkbox"
                          className="form-check-input"
                          checked={pdfHiddenMetrics.diferencia}
                          disabled={!pdfHiddenMetrics.diferencia && pdfVisibleMetricCount === 1}
                          onChange={() => togglePdfMetric("diferencia")}
                        />
                        <Label className="form-check-label ms-2" htmlFor="pdf-hide-dif">
                          Ocultar columna DIF.
                        </Label>
                      </div>
                    </DropdownItem>
                    <DropdownItem divider />
                    <DropdownItem onClick={() => handleExportPdf("stock-por-articulo")}>
                      <i className="ri-file-list-3-line me-2"></i>
                      Control de stock por artículo
                    </DropdownItem>
                    <DropdownItem onClick={() => handleExportPdf("movimientos-por-fecha")}>
                      <i className="ri-calendar-event-line me-2"></i>
                      Movimientos por fecha
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => handleExportPdf("stock-por-familia-linea")}
                      disabled={!hasAmountMode}
                    >
                      <i className="ri-node-tree me-2"></i>
                      Resultado por familia y línea
                    </DropdownItem>
                    <DropdownItem onClick={() => handleExportPdf("all")}>
                      <i className="ri-file-copy-2-line me-2"></i>
                      Todos
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                {canViewCollections && (
                  <Button
                    color="dark"
                    className="btn-soft-dark"
                    onClick={() => setIsCollectionsModalOpen(true)}
                  >
                    <i className="ri-settings-3-line me-1"></i>
                    Colecciones
                  </Button>
                )}
                <Button color="dark" className="btn-soft-dark" onClick={() => setFiltersOpen(true)}>
                  <i className="ri-equalizer-line me-1"></i>
                  Filtros
                </Button>
              </div>
            </div>
        </CardHeader>
          <CardBody>
            {recordsError && (
              <div className="alert alert-danger mb-3">
                {recordsError}
              </div>
            )}
            {recordsLoading && !recordsError ? (
              <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                <Spinner color="primary" />
                <small className="mt-2">Cargando...</small>
              </div>
            ) : (
              <>
            <div className="d-flex flex-wrap flex-lg-nowrap gap-2 mb-3">
              <div style={{ minWidth: 140, flex: "1 1 140px" }}>
                <FormGroup className="mb-0">
                  <Label className="text-muted text-uppercase small fw-semibold">
                    Vista
                  </Label>
                  <Input
                    type="select"
                    value={groupViewMode}
                    onChange={(event) =>
                      setGroupViewMode(event.target.value as "sin" | "coleccion")
                    }
                  >
                    <option value="sin">Sin colección</option>
                    <option value="coleccion">Por colección</option>
                  </Input>
                </FormGroup>
              </div>
              <div style={{ minWidth: 150, flex: "1 1 150px" }}>
                <FormGroup className="mb-0">
                  <Label className="text-muted text-uppercase small fw-semibold">
                    Sucursal vista
                    {branchFilter.length > 1 ? (
                      <span className="badge bg-warning-subtle text-warning ms-2">
                        {branchFilter.length}
                      </span>
                    ) : null}
                  </Label>
                  <Input
                    type="select"
                    value={branchView}
                    onChange={(event) => setBranchView(event.target.value)}
                    disabled={branchFilter.length <= 1}
                    className={branchFilter.length > 1 ? "border-warning" : undefined}
                  >
                    <option value="todas">Todas (global)</option>
                    {branchFilter.map((branch) => (
                      <option key={branch} value={branch}>
                        {branch}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </div>
              <div className="flex-grow-1" style={{ minWidth: 180, flex: "2 1 180px" }}>
                <FormGroup className="mb-0">
                  <Label className="text-muted text-uppercase small fw-semibold">
                    Buscar artículo
                  </Label>
                  <Input
                    type="search"
                    placeholder="ID, descripción..."
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                </FormGroup>
              </div>
              <div style={{ minWidth: 150, flex: "1 1 150px", order: 98 }}>
                <FormGroup className="mb-0">
                  <Label className="text-muted text-uppercase small fw-semibold">
                    IDMOV Inicial
                    <span className="badge border border-warning bg-transparent text-body ms-2">
                      {inventoryHeaderMovementIds.inventarioInicial.length}
                    </span>
                  </Label>
                  <Dropdown
                    isOpen={idmovInitialOpen}
                    toggle={() => setIdmovInitialOpen((prev) => !prev)}
                  >
                    <DropdownToggle
                      color="outline-secondary"
                      className="w-100 d-flex align-items-center justify-content-between border border-warning bg-transparent text-body"
                      disabled={
                        filtersDisabled ||
                        inventoryHeaderMovementIds.inventarioInicial.length === 0
                      }
                    >
                      <span>{getIdmovLabel("initial")}</span>
                      <i className="ri-arrow-down-s-line" />
                    </DropdownToggle>
                    <DropdownMenu className="w-100">
                      {branchView === "todas" && branchFilter.length > 1 ? (
                        <>
                          <DropdownItem
                            onClick={() =>
                              setInventoryMovementByBranch((prev) => ({
                                ...prev,
                                initial: {},
                              }))
                            }
                          >
                            Todos (sumar)
                          </DropdownItem>
                          {inventoryHeaderMovementGroups.inventarioInicial.map((group) => (
                            <div key={group.branch}>
                              <DropdownItem header>
                                <i className="ri-store-2-line me-2 text-muted" />
                                {group.branch}
                              </DropdownItem>
                              {group.ids.map((id) => (
                                <DropdownItem
                                  key={`${group.branch}-init-${id}`}
                                  onClick={() => {
                                    const key = normalizeBranchKey(group.branch);
                                    setInventoryMovementByBranch((prev) => ({
                                      ...prev,
                                      initial: {
                                        ...prev.initial,
                                        [key]: id,
                                      },
                                    }));
                                  }}
                                  active={
                                    inventoryMovementByBranch.initial[
                                      normalizeBranchKey(group.branch)
                                    ] === id
                                  }
                                  disabled={group.ids.length === 1}
                                >
                                  {inventoryMovementByBranch.initial[
                                    normalizeBranchKey(group.branch)
                                  ] === id && (
                                    <i className="ri-check-line me-2 text-success" />
                                  )}
                                  {id}
                                </DropdownItem>
                              ))}
                            </div>
                          ))}
                        </>
                      ) : (
                        <>
                          <DropdownItem
                            onClick={() =>
                              handleInventoryMovementFilterChange("initial", "")
                            }
                          >
                            Todos (sumar)
                          </DropdownItem>
                          {inventoryHeaderMovementIds.inventarioInicial.map((id) => (
                            <DropdownItem
                              key={id}
                              onClick={() =>
                                handleInventoryMovementFilterChange("initial", id)
                              }
                            >
                              {`IDMOV: ${id}`}
                            </DropdownItem>
                          ))}
                        </>
                      )}
                    </DropdownMenu>
                  </Dropdown>
                </FormGroup>
              </div>
              <div style={{ minWidth: 150, flex: "1 1 150px", order: 99 }}>
                <FormGroup className="mb-0">
                  <Label className="text-muted text-uppercase small fw-semibold">
                    IDMOV Final
                    <span className="badge border border-warning bg-transparent text-body ms-2">
                      {inventoryHeaderMovementIds.inventarioFinal.length}
                    </span>
                  </Label>
                  <Dropdown
                    isOpen={idmovFinalOpen}
                    toggle={() => setIdmovFinalOpen((prev) => !prev)}
                  >
                    <DropdownToggle
                      color="outline-secondary"
                      className="w-100 d-flex align-items-center justify-content-between border border-warning bg-transparent text-body"
                      disabled={
                        filtersDisabled ||
                        inventoryHeaderMovementIds.inventarioFinal.length === 0
                      }
                    >
                      <span>{getIdmovLabel("final")}</span>
                      <i className="ri-arrow-down-s-line" />
                    </DropdownToggle>
                    <DropdownMenu className="w-100">
                      {branchView === "todas" && branchFilter.length > 1 ? (
                        <>
                          <DropdownItem
                            onClick={() =>
                              setInventoryMovementByBranch((prev) => ({
                                ...prev,
                                final: {},
                              }))
                            }
                          >
                            Todos (sumar)
                          </DropdownItem>
                          {inventoryHeaderMovementGroups.inventarioFinal.map((group) => (
                            <div key={group.branch}>
                              <DropdownItem header>
                                <i className="ri-store-2-line me-2 text-muted" />
                                {group.branch}
                              </DropdownItem>
                              {group.ids.map((id) => (
                                <DropdownItem
                                  key={`${group.branch}-final-${id}`}
                                  onClick={() => {
                                    const key = normalizeBranchKey(group.branch);
                                    setInventoryMovementByBranch((prev) => ({
                                      ...prev,
                                      final: {
                                        ...prev.final,
                                        [key]: id,
                                      },
                                    }));
                                  }}
                                  active={
                                    inventoryMovementByBranch.final[
                                      normalizeBranchKey(group.branch)
                                    ] === id
                                  }
                                  disabled={group.ids.length === 1}
                                >
                                  {inventoryMovementByBranch.final[
                                    normalizeBranchKey(group.branch)
                                  ] === id && (
                                    <i className="ri-check-line me-2 text-success" />
                                  )}
                                  {id}
                                </DropdownItem>
                              ))}
                            </div>
                          ))}
                        </>
                      ) : (
                        <>
                          <DropdownItem
                            onClick={() =>
                              handleInventoryMovementFilterChange("final", "")
                            }
                          >
                            Todos (sumar)
                          </DropdownItem>
                          {inventoryHeaderMovementIds.inventarioFinal.map((id) => (
                            <DropdownItem
                              key={id}
                              onClick={() =>
                                handleInventoryMovementFilterChange("final", id)
                              }
                            >
                              {`IDMOV: ${id}`}
                            </DropdownItem>
                          ))}
                        </>
                      )}
                    </DropdownMenu>
                  </Dropdown>
                </FormGroup>
              </div>
              <div style={{ minWidth: 150, flex: "1 1 150px" }}>
                <FormGroup className="mb-0">
                  <Label className="text-muted text-uppercase small fw-semibold">
                    Colección
                  </Label>
                  <Input
                    type="select"
                    value={collectionFilter}
                    onChange={(event) => setCollectionFilter(event.target.value)}
                    disabled={filtersDisabled || collectionGroups.length === 0}
                  >
                    <option value="todas">Todas las colecciones</option>
                    <option value={NULL_OPTION_VALUE}>Sin colección</option>
                    {collectionGroups.map((collection) => (
                      <option key={collection.id} value={String(collection.id)}>
                        {collection.nombre}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </div>
              <div style={{ minWidth: 140, flex: "1 1 140px" }}>
                <FormGroup className="mb-0">
                  <Label className="text-muted text-uppercase small fw-semibold">
                    Familia
                  </Label>
                  <Input
                    id="idmov-inicial-select"
                    type="select"
                    value={familyFilter}
                    onChange={(event) => setFamilyFilter(event.target.value)}
                    disabled={
                      filtersDisabled ||
                      !serverFilter ||
                      familyOptions.length === 0
                    }
                  >
                    {familyOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </div>
              <div style={{ minWidth: 140, flex: "1 1 140px" }}>
                <FormGroup className="mb-0">
                  <Label className="text-muted text-uppercase small fw-semibold">
                    Línea
                  </Label>
                  <Input
                    id="idmov-final-select"
                    type="select"
                    value={lineFilter}
                    onChange={(event) => setLineFilter(event.target.value)}
                    disabled={
                      filtersDisabled ||
                      !serverFilter ||
                      lineOptions.length === 0
                    }
                  >
                    {lineOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </div>
            </div>
            <StockTabsNav
              activeTab={activeStockTab}
              onChange={setActiveStockTab}
            />
            <TabContent activeTab={activeStockTab}>
              <TabPane tabId="resumen">
                <SummaryAccordion
                  cards={summaryCards}
                  isOpen={isSummaryOpen}
                  onToggle={() => setIsSummaryOpen((prev) => !prev)}
                  isDisabled={isSummaryDisabled}
                />
                {agentSummaryTable && (
                  <Card className="mt-3">
                    <CardHeader className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="mb-0">{agentSummaryTable.title}</h6>
                        <small className="text-muted">
                          {agentSummaryTable.rows.length} filas
                        </small>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          color="dark"
                          className="btn-soft-dark"
                          size="sm"
                          disabled={agentSummaryPage <= 1}
                          onClick={() =>
                            setAgentSummaryPage((prev) => Math.max(1, prev - 1))
                          }
                        >
                          Anterior
                        </Button>
                        <small className="text-muted">
                          {agentSummaryPage} /{" "}
                          {Math.max(1, Math.ceil(agentSummaryTable.rows.length / 15))}
                        </small>
                        <Button
                          color="dark"
                          className="btn-soft-dark"
                          size="sm"
                          disabled={
                            agentSummaryPage >=
                            Math.max(1, Math.ceil(agentSummaryTable.rows.length / 15))
                          }
                          onClick={() =>
                            setAgentSummaryPage((prev) =>
                              Math.min(
                                Math.max(
                                  1,
                                  Math.ceil(agentSummaryTable.rows.length / 15),
                                ),
                                prev + 1,
                              ),
                            )
                          }
                        >
                          Siguiente
                        </Button>
                      </div>
                    </CardHeader>
                    <CardBody>
                      <div
                        className="table-responsive"
                        style={{ maxHeight: 360, overflow: "auto" }}
                      >
                        <Table className="table table-sm align-middle mb-0 control-stock-sticky-table">
                          <thead>
                            <tr>
                              {agentSummaryTable.columns.map((col) => (
                                <th key={col}>{col}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {agentSummaryTable.rows
                              .slice(
                                (agentSummaryPage - 1) * 15,
                                agentSummaryPage * 15,
                              )
                              .map((row, rowIndex) => (
                                <tr key={`agent-row-${rowIndex}`}>
                                  {agentSummaryTable.columns.map((col) => (
                                    <td key={`${rowIndex}-${col}`}>
                                      {row?.[col] ?? "—"}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                )}
                {showDifferenceChart && (
                  <Card className="mt-3">
                    <CardHeader className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="mb-0">Diferencias vs Teórico</h6>
                        <small className="text-muted">
                          {differenceChartRows.length} artículos
                        </small>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          color="dark"
                          className="btn-soft-dark"
                          size="sm"
                          disabled={differenceChartPage <= 1}
                          onClick={() =>
                            setDifferenceChartPage((prev) => Math.max(1, prev - 1))
                          }
                        >
                          Anterior
                        </Button>
                        <small className="text-muted">
                          {differenceChartPage} / {differenceChartPageCount}
                        </small>
                        <Button
                          color="dark"
                          className="btn-soft-dark"
                          size="sm"
                          disabled={differenceChartPage >= differenceChartPageCount}
                          onClick={() =>
                            setDifferenceChartPage((prev) =>
                              Math.min(differenceChartPageCount, prev + 1),
                            )
                          }
                        >
                          Siguiente
                        </Button>
                      </div>
                    </CardHeader>
                    <CardBody>
                      {differenceChartRows.length === 0 ? (
                        <div className="text-muted">
                          No hay diferencias para mostrar en el período seleccionado.
                        </div>
                      ) : (
                        <div style={{ width: "100%", minHeight: 320 }}>
                          <ReactECharts
                            option={differenceChartOption}
                            style={{ height: 320, width: "100%" }}
                            opts={{ renderer: "canvas" }}
                          />
                        </div>
                      )}
                    </CardBody>
                  </Card>
                )}
              </TabPane>
            <TabPane tabId="stock-por-articulo">
                <TableContainer
                  columns={columns}
                  data={displayData}
                  customPageSize={10}
                  emptyMessage="Seleccioná servidor y periodo para ver resultados."
                  divClass="table-responsive control-stock-main-table"
                  tableClass="table align-top table-striped mb-0 control-stock-sticky-table"
                  theadClass={
                    displayData.length === 0 ? "table-light text-muted" : "table-light"
                  }
                  rowClassName={(row) => {
                    if (row.original?.__isCollectionHeader) {
                      return "table-light text-muted";
                    }
                    if (row.original?.__isCollectionSubtotal) {
                      return "text-uppercase";
                    }
                    return "";
                  }}
                  rowColSpan={(row) => !!row.original?.__isCollectionHeader}
                  rowCellRenderer={(row) => {
                    if (!row.original?.__isCollectionHeader) return null;
                    const label = row.original?.__collectionLabel ?? "Sin colección";
                    const diffValue = Number(row.original?.__collectionDiff ?? 0);
                    const diffText =
                      diffValue > 0
                        ? `+${formatNumber(diffValue)}`
                        : formatNumber(diffValue);
                    const diffClass =
                      diffValue > 0 ? "text-success" : diffValue < 0 ? "text-danger" : "text-muted";
                    return (
                      <div className="d-flex justify-content-between align-items-center w-100 text-uppercase fw-semibold">
                        <span>{label}</span>
                        <span>
                          (DIF: <span className={diffClass}>{diffText}</span>)
                        </span>
                      </div>
                    );
                  }}
                />
              </TabPane>
              <TabPane tabId="stock-por-familia-linea">
                {!hasAmountMode ? (
                  <div className="alert alert-info mb-0">
                    Cambiá <strong>Modo de vista</strong> a <strong>Montos (PYG)</strong> o <strong>Cantidades + Montos</strong> para habilitar este resumen.
                  </div>
                ) : (
                  <TableContainer
                    columns={familyLineColumns}
                    data={familyLineResults}
                    customPageSize={10}
                    emptyMessage="Seleccioná servidor y periodo para ver resultados."
                    divClass="table-responsive"
                    tableClass="table align-middle table-striped mb-0 control-stock-sticky-table"
                    theadClass={
                      familyLineResults.length === 0
                        ? "table-light text-muted"
                        : "table-light"
                    }
                  />
                )}
              </TabPane>
              <TabPane tabId="stock-por-sucursal">
                {!canUseBranchSummaryTab ? (
                  <div className="alert alert-info mb-0">
                    Dejá el filtro de sucursales vacío y seleccioná un <strong>modo con monto</strong> para habilitar este resumen.
                  </div>
                ) : (
                  <TableContainer
                    columns={branchColumns}
                    data={branchResults}
                    customPageSize={10}
                    emptyMessage="Seleccioná servidor y periodo para ver resultados."
                    divClass="table-responsive"
                    tableClass="table align-middle table-striped mb-0 control-stock-sticky-table"
                    theadClass={
                      branchResults.length === 0
                        ? "table-light text-muted"
                        : "table-light"
                    }
                  />
                )}
            </TabPane>
            <TabPane tabId="movimientos-por-fecha">
                <MovementByDateTab
                  movementDateColumns={movementDateColumns}
                  movementRowsByDate={movementRowsByDate}
                  movementDateAvailability={movementDateAvailability}
                  movementDateMetric={movementDateMetric}
                  onMovementDateMetricChange={setMovementDateMetric}
                  metricMode={metricMode}
                />
            </TabPane>
            </TabContent>
              </>
            )}
          </CardBody>
        </Card>
      </Container>
      <DetailsModal
        isOpen={isDetailsModalOpen}
        onToggle={handleCloseDetailsModal}
        detailsMetric={detailsMetric}
        setDetailsMetric={setDetailsMetric}
        detailTable={detailTable}
        detailMetricAvailability={detailMetricAvailability}
        allDetailRows={detailTableAllRows}
        highlightArticleId={detailArticleTarget?.idArticulo ?? null}
      />
      <SyncModal
        isOpen={isSyncModalOpen}
        onToggle={() => setIsSyncModalOpen(false)}
        onStartSync={handleStartSync}
        isStarting={syncingDays !== null}
        startingDays={syncingDays}
        diagnostico={syncDiagnostico}
        isLoading={syncDiagnosticoLoading}
        loadError={syncStatusError}
        actionError={syncActionError}
      />
      <CollectionsModal
        isOpen={isCollectionsModalOpen}
        onClose={() => setIsCollectionsModalOpen(false)}
        canView={canViewCollections}
        canEdit={canEditCollections}
        canClone={canCloneCollections}
      />
      <ControlStockFiltersDrawer
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        serverOptions={serverOptions}
        serverFilter={serverFilter}
        onServerFilterChange={setServerFilter}
        branchOptions={branchOptions}
        branchFilter={branchFilter}
        onBranchFilterChange={setBranchFilter}
        controlMode={controlMode}
        onControlModeChange={setControlMode}
        differenceFilter={differenceFilter}
        onDifferenceFilterChange={setDifferenceFilter}
        metricMode={metricMode}
        onMetricModeChange={setMetricMode}
        dateModeDropdownOpen={dateModeDropdownOpen}
        toggleDateModeDropdown={toggleDateModeDropdown}
        currentModeOption={currentModeOption}
        dateSelectionMode={dateSelection.mode}
        onModeChange={handleModeChange}
        renderDateModeInput={renderDateModeInput}
      />
      <AgentWidget
        isOpen={isAgentOpen}
        onToggle={() => setIsAgentOpen((prev) => !prev)}
        informeKey="control_stock"
        snapshotPayload={agentSnapshotPayload}
        snapshotReady={agentSnapshotReady}
        onApplyFilters={applyAgentFilters}
        onApplyTab={(tabId) => setActiveStockTab(tabId as StockTabId)}
        collectionPermissions={{
          canView: canViewCollections,
          canEdit: canEditCollections,
          canClone: canCloneCollections,
        }}
        onApplyUiAction={(action) => {
          const handleCollectionUpdate = async () => {
            if (action.tipo === "coleccion_crear") {
              const nombre = String(action.nombre ?? action.valor ?? "").trim();
              if (!nombre) return;
              await requestJson("/api/informes/colecciones", {
                method: "POST",
                body: JSON.stringify({
                  nombre,
                  descripcion: action.descripcion ?? null,
                }),
              });
              setCollectionsRefreshKey((prev) => prev + 1);
              return;
            }
            if (action.tipo === "coleccion_editar") {
              const id = Number(action.id ?? action.coleccionId ?? action.valor);
              if (!id) return;
              await requestJson(`/api/informes/colecciones/${id}`, {
                method: "PUT",
                body: JSON.stringify({
                  nombre: action.nombre ?? action.valueNombre ?? action.valorNombre ?? null,
                  descripcion: action.descripcion ?? null,
                  orden: action.orden ?? null,
                }),
              });
              setCollectionsRefreshKey((prev) => prev + 1);
              return;
            }
            if (action.tipo === "coleccion_eliminar") {
              const id = Number(action.id ?? action.coleccionId ?? action.valor);
              if (!id) return;
              await requestJson(`/api/informes/colecciones/${id}`, {
                method: "DELETE",
              });
              setCollectionsRefreshKey((prev) => prev + 1);
              return;
            }
            if (action.tipo === "coleccion_orden") {
              const id = Number(action.id ?? action.coleccionId);
              const orden = Number(action.orden ?? action.valor);
              if (!id || !Number.isFinite(orden)) return;
              await requestJson(`/api/informes/colecciones/${id}`, {
                method: "PUT",
                body: JSON.stringify({
                  nombre: action.nombre ?? null,
                  descripcion: action.descripcion ?? null,
                  orden,
                }),
              });
              setCollectionsRefreshKey((prev) => prev + 1);
              return;
            }
            if (action.tipo === "coleccion_asignar_articulo") {
              const coleccionId = Number(action.coleccionId ?? action.id);
              const articuloId = String(action.articuloId ?? action.valor ?? "").trim();
              if (!coleccionId || !articuloId) return;
              const items = await requestJson(
                `/api/informes/colecciones/${coleccionId}/articulos`,
              );
              const list = Array.isArray(items)
                ? items.map((i) => String(i).trim()).filter(Boolean)
                : [];
              if (!list.includes(articuloId)) {
                list.push(articuloId);
                await requestJson(`/api/informes/colecciones/${coleccionId}/articulos`, {
                  method: "PUT",
                  body: JSON.stringify({ articulos: list }),
                });
                setCollectionsRefreshKey((prev) => prev + 1);
              }
              return;
            }
            if (action.tipo === "coleccion_quitar_articulo") {
              const coleccionId = Number(action.coleccionId ?? action.id);
              const articuloId = String(action.articuloId ?? action.valor ?? "").trim();
              if (!coleccionId || !articuloId) return;
              const items = await requestJson(
                `/api/informes/colecciones/${coleccionId}/articulos`,
              );
              const list = Array.isArray(items)
                ? items.map((i) => String(i).trim()).filter(Boolean)
                : [];
              const updated = list.filter((item) => item !== articuloId);
              await requestJson(`/api/informes/colecciones/${coleccionId}/articulos`, {
                method: "PUT",
                body: JSON.stringify({ articulos: updated }),
              });
              setCollectionsRefreshKey((prev) => prev + 1);
              return;
            }
            if (action.tipo === "coleccion_clonar") {
              const coleccionId = Number(action.coleccionIdOrigen ?? action.id ?? action.valor);
              if (!coleccionId) return;
              await requestJson("/api/informes/colecciones/clonar", {
                method: "POST",
                body: JSON.stringify({ coleccionIdOrigen: coleccionId }),
              });
              setCollectionsRefreshKey((prev) => prev + 1);
              return;
            }
            if (action.tipo === "coleccion_clonar_usuario") {
              const usuarioDestinoId = Number(action.usuarioDestinoId ?? action.usuarioId);
              const coleccionIds = Array.isArray(action.coleccionIds)
                ? action.coleccionIds.map((id: any) => Number(id)).filter((id: number) => id)
                : [];
              if (!usuarioDestinoId || coleccionIds.length === 0) return;
              await requestJson("/api/informes/colecciones/clonar-usuario", {
                method: "POST",
                body: JSON.stringify({ usuarioDestinoId, coleccionIds }),
              });
              return;
            }
          };

          if (action.tipo === "descargar_excel" && action.valor) {
            handleExportExcel(action.valor);
            return;
          }
          if (action.tipo === "descargar_pdf" && action.valor) {
            handleExportPdf(action.valor);
            return;
          }
          if (action.tipo === "ver_movimientos") {
            setDetailArticleTarget(null);
            setIsDetailsModalOpen(true);
            return;
          }
          if (action.tipo === "agente_tabla_resumen") {
            const columns = Array.isArray(action.columns)
              ? action.columns.map((col: any) => String(col))
              : [];
            const rows = Array.isArray(action.rows) ? action.rows : [];
            if (columns.length > 0 && rows.length > 0) {
              setAgentSummaryTable({
                title: action.title || "Resultado del agente",
                columns,
                rows,
              });
              setAgentSummaryPage(1);
              setActiveStockTab("resumen");
            }
            return;
          }
          if (action.tipo === "pdf_ocultar_columna") {
            const campo = String(action.campo ?? action.valor ?? "");
            if (!campo) return;
            if (campo === "costo") {
              setPdfHideCosto((prev) => !prev);
              return;
            }
            if (
              campo === "inventarioInicial" ||
              campo === "compras" ||
              campo === "remisionPositiva" ||
              campo === "remisionNegativa" ||
              campo === "ventas" ||
              campo === "ajusteNegativo" ||
              campo === "teorico" ||
              campo === "inventarioFinal" ||
              campo === "diferencia"
            ) {
              togglePdfMetric(campo as keyof typeof pdfHiddenMetrics);
            }
            return;
          }
          if (String(action.tipo || "").startsWith("coleccion_")) {
            void handleCollectionUpdate();
          }
        }}
      />
    </div>
  );
};

export default InformesControlStockPage;
